import React, {
  Profiler,
  Suspense,
  useCallback,
  lazy,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { registerTutorialTarget, unregisterTutorialTarget } from '@/components/tutorial/PostOnboardingTutorial';
import {
  Alert,
  View,
  Image,
  Pressable,
  Platform,
  FlatList,
  RefreshControl,
  ListRenderItem,
  Modal,
  ActivityIndicator,
  InteractionManager,
  StyleSheet,
  type ViewToken,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useGlobalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { usePremiumGateStore, type PremiumGatePayload } from '@/state/premiumGateStore';
import { useToast } from '@/components/ui/ToastProvider';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import { LoadingState, SuccessAnimation } from '@/components/ui/LoadingState';
import {
  type TripRecommendation,
} from '@/services/aiPlanner';
import {
  ensureTripForUser,
  subscribeToUserTrips,
  getLastKnownTrips,
  refreshTripListFromServer,
  getTripById,
  notifyLiveTripUpdated,
  type Trip,
  updateTripLandmarkImage,
  createTrip,
  type CreateTripData,
} from '@/services/trips';
import { isUiDebugLoggingEnabled } from '@/services/logger';
import { networkMonitor } from '@/services/networkMonitor';
import { getCachedTrips } from '@/services/offlineTrips';
import { formatTripDates, calculateDaysUntil, type Trip as TripType } from '@/data/trips';
import { ManualTripForm, type ManualTripFormPrefill } from '@/components/trips/ManualTripForm';
import { EditTripForm } from '@/components/trips/EditTripForm';
import { fetchAndCacheLandmarkImage, getLandmarkImage } from '@/utils/landmarkImages';
import { schedulePresentAfterAuthSessionDismiss } from '@/utils/schedulePresentAfterAuthSessionDismiss';
import { checkGmailConnection, initiateGmailAuth } from '@/services/gmailSync';
import {
  checkInboxStatus,
  initiateInboxAuth,
  syncInboxBookings,
  disconnectEmailProvider,
  type InboxProviderId,
} from '@/services/inboxSync';
import { useInboxConnectionsStore } from '@/state/inboxConnectionsStore';
import { SavedRecommendationsList } from '@/components/recommendations/SavedRecommendationsList';
import * as WebBrowser from 'expo-web-browser';
import { SavedRecommendationDetailModal } from '@/components/recommendations/SavedRecommendationDetailModal';
import { CollaborationModal } from '@/components/trips/CollaborationModal';
import { TripCardEnhanced } from '@/components/trips/TripCardEnhanced';
import { TripStatusBadge } from '@/components/trips/TripStatusBadge';
import { EmptyTripsState } from '@/components/trips/EmptyTripsState';
import type { TripActionsModalCompletion } from '@/components/trips/TripActionsModal';
import { TripCardSkeleton } from '@/components/ui/SkeletonLoader';
import { generateAllTripAlerts, type TripAlert } from '@/utils/tripAlerts';
import { TemplateBrowser } from '@/components/trips/TemplateBrowser';
import { TemplateDetailModal } from '@/components/trips/TemplateDetailModal';
import { TemplateCustomizationModal } from '@/components/trips/TemplateCustomizationModal';
import { TemplatesQuickWidget } from '@/components/trips/TemplatesQuickWidget';
import { createTripFromTemplate, getTemplateById } from '@/services/tripTemplates';
import { createItineraryFromRecommendationHighlights } from '@/services/recommendationItinerary';
import { shareTemplate } from '@/services/templateShare';
import type { TripTemplate } from '@/types/tripTemplates';
import type { SavedRecommendation } from '@/services/recommendations';
import { cleanupDuplicateTemplates } from '@/utils/cleanupDuplicateTemplates';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { getDocumentsTabTarget, getMapsTabTarget } from '@/navigation/tabTargets';
import { useTripBudgetSummaries } from '@/hooks/useTripBudgetSummaries';
import { useBudgetAlertNotifications } from '@/hooks/useBudgetAlertNotifications';
import { useWeatherAlerts } from '@/hooks/useWeatherAlerts';
import { DocumentExpirationWidget } from '@/components/documents/DocumentExpirationWidget';
import { CombinedSearchFilterBar } from '@/components/trips/CombinedSearchFilterBar';
import { type TripFilterType } from '@/components/trips/TripFilterBar';
import { TripListSectionHeader } from '@/components/trips/TripListSectionHeader';
import { QuickActionsWidget } from '@/components/trips/QuickActionsWidget';
import type { ExtractedBooking } from '@/services/inboxSync';
import { ActiveTripStatusCard } from '@/components/trips/ActiveTripStatusCard';
import { ActiveTripBottomSheet } from '@/components/trips/ActiveTripBottomSheet';
import { ActiveTripPeek } from '@/components/trips/ActiveTripPeek';
import { HelpAndFeedbackCard } from '@/components/settings/HelpAndFeedbackCard';
import { NewTripOptionsModal } from '@/components/trips/NewTripOptionsModal';
import { EmailProviderSelectionModal } from '@/components/trips/EmailProviderSelectionModal';
import { useUpcomingItineraryHighlights } from '@/hooks/useUpcomingItineraryHighlights';
import { useAppleTripSurfaceTripsSync } from '@/hooks/useAppleTripSurfaceTripsSync';
import { useDayBriefing } from '@/hooks/useDayBriefing';
import { useDayBriefingNotifications } from '@/hooks/useDayBriefingNotifications';
import { subscribeToTripBookings, type Booking } from '@/services/bookings';
import { subscribeToTripCardStates, setTripCardVisibility, type TripCardState, type TripCardVisibility } from '@/services/tripCardStates';
import { isTripCompleted } from '@/utils/tripStatus';
import { addBreadcrumb, recordCrashlyticsNonFatal } from '@/services/errorTracking';
import { refreshTripLandmarkImage } from '@/services/trips';
import { clearInboxOauthMarkers, setInboxOauthPending, setInboxOauthSuccess } from '@/utils/inboxOauth';
import { setLastUsedInboxProvider } from '@/utils/lastInboxProvider';
import { completeOAuthWebSession } from '@/utils/completeOAuthWebSession';
import { useInboxOauthResume } from '@/hooks/useInboxOauthResume';
import { useInboxOauthPostMessage } from '@/hooks/useInboxOauthPostMessage';
import { useTemplateModalState } from '@/hooks/useTemplateModalState';
import {
  normalizeTemplateIdParam,
  normalizeTemplateTopicParam,
} from '@/navigation/templateDeepLink';
import { logTemplateDeepLinkAnalytics } from '@/services/appleDeepLinkSurface';
import { maybePromptForRating } from '@/services/ratingPrompt';
import { maybePromptNotificationValue } from '@/services/retentionNotificationPrompt';
import {
  getTripDestinationSearchText,
  getTripLandmarkDestination,
  getTripPrimaryDestination,
} from '@/utils/tripDestination';
import { getTravelRequirements, type TravelRequirements } from '@/services/travelRequirements';
import { toIso2CountryCode } from '@/utils/countryIso2';
import {
  canCreateAnotherActiveTrip,
  countOwnedActiveTripsForFreeLimit,
  hasPremiumAccess,
  isPremiumGatePresentedError,
  PremiumGatePresentedError,
  TEMPLATE_CREATE_IN_PROGRESS_REASON,
} from '@/services/entitlements';
import * as Linking from 'expo-linking';
import { formatDistanceToNow } from 'date-fns';
import { NotificationBell } from '@/notifications/components/NotificationBell';
import { useTripDetectionSessionStore } from '@/state/tripDetectionSessionStore';
import { NotificationModal } from '@/notifications/components/NotificationModal';
import {
  dismissNotification,
  useNotificationCount,
  useNotifications as useNotificationList,
  useTripNotificationSync,
} from '@/notifications/service';
import { useNotificationActionHandler } from '@/notifications/hooks';
import type { Notification } from '@/notifications/types';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';
import { logAnalyticsEvent } from '@/services/analyticsLogger';

traceModuleEvaluation('app/(tabs)/index');

const TripActionsModal = lazy(() =>
  import('@/components/trips/TripActionsModal').then((module) => ({ default: module.TripActionsModal }))
);
const AITravelAssistantModal = lazy(() =>
  import('@/components/trips/AITravelAssistant').then((module) => ({ default: module.AITravelAssistant }))
);
const SmartUploadModalLazy = lazy(() =>
  import('@/components/smartUpload/SmartUploadModal').then((module) => ({ default: module.SmartUploadModal }))
);
const EmailSyncStatusModal = lazy(() =>
  import('@/components/planning/EmailSyncStatus').then((module) => ({ default: module.EmailSyncStatus }))
);
const EmailSyncResultsModalLazy = lazy(() =>
  import('@/components/planning/EmailSyncResultsModal').then((module) => ({ default: module.EmailSyncResultsModal }))
);
const TemplateBuilderLazy = lazy(() =>
  import('@/components/templates/TemplateBuilder').then((module) => ({ default: module.TemplateBuilder }))
);

const ACTIVE_TRIP_CARD_GRACE_PERIOD_MS = 24 * 60 * 60 * 1000;
const TRAVEL_REQUIREMENTS_CACHE_TTL_MS = 14 * 24 * 60 * 60 * 1000; // align with service cache
const INITIAL_VISIBLE_BUDGET_ROWS = 4;
const HEAVY_WIDGET_DEFER_MS = 900;
const FOOTER_WIDGET_END_REACHED_THRESHOLD = 0.35;

const PROVIDER_LABELS: Record<InboxProviderId, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  icloud: 'iCloud',
};

const confirmDisconnectProvider = (provider: InboxProviderId): Promise<boolean> => {
  const label = PROVIDER_LABELS[provider];
  const message = `Disconnect ${label}?\n\nYour existing trips stay in Tripvio. You can reconnect this email anytime.`;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return Promise.resolve(window.confirm(message));
  }
  return new Promise((resolve) => {
    Alert.alert(`Disconnect ${label}?`, 'Your existing trips stay in Tripvio. You can reconnect this email anytime.', [
      { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
      { text: 'Disconnect', style: 'destructive', onPress: () => resolve(true) },
    ]);
  });
};

const BOOKING_TYPE_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  flight: 'airplane',
  hotel: 'bed',
  car: 'car',
  restaurant: 'restaurant',
  activity: 'ticket',
  shopping: 'bag-handle',
  transportation: 'bus',
};

type DeferredWidgetPlaceholderProps = {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  showSpinner?: boolean;
  statusMessage?: string;
  actionLabel?: string;
  onAction?: () => void;
};

function DeferredWidgetPlaceholder({
  icon,
  title,
  description,
  showSpinner = true,
  statusMessage = 'Loads once this section is on screen.',
  actionLabel,
  onAction,
}: DeferredWidgetPlaceholderProps) {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      style={{
        backgroundColor: theme.colors.backgroundSecondary,
        borderColor: theme.colors.borderMuted,
      }}
    >
      <View style={{ gap: theme.spacing(1.25) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: theme.radii.pill,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.colors.primary + '12',
            }}
          >
            <Ionicons name={icon} size={18} color={theme.colors.primary} />
          </View>
          <SubHeading>{title}</SubHeading>
        </View>
        <Body color={theme.colors.textSecondary}>{description}</Body>
        {showSpinner ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
            <Caption color={theme.colors.textSecondary}>{statusMessage}</Caption>
          </View>
        ) : null}
        {actionLabel && onAction ? (
          <Button label={actionLabel} variant="secondary" onPress={onAction} />
        ) : null}
      </View>
    </Card>
  );
}

function DeferredInlineWidgetPlaceholder({ message }: { message: string }) {
  const theme = useTheme();

  return (
    <View style={{ padding: theme.spacing(2), paddingTop: 0, gap: theme.spacing(1) }}>
      <Body color={theme.colors.textSecondary}>{message}</Body>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
        <ActivityIndicator size="small" color={theme.colors.primary} />
        <Caption color={theme.colors.textSecondary}>
          Loads once this section is on screen.
        </Caption>
      </View>
    </View>
  );
}

function inboxCallableRateLimitUserMessage(error: unknown): string | null {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code?: string }).code)
      : '';
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message?: unknown }).message)
        : String(error);
  if (code !== 'functions/resource-exhausted' && !/rate limit exceeded/i.test(message)) {
    return null;
  }
  const secondsMatch = message.match(/(\d+)\s*seconds?/i);
  if (secondsMatch) {
    return `Too many inbox requests. Try again in about ${secondsMatch[1]} seconds.`;
  }
  return 'Too many inbox requests. Please wait a few minutes and try again.';
}

function TripsScreenInner() {
  const theme = useTheme();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const supportsBottomSheet = Platform.OS !== 'web';
  const profile = useUserStore((state) => state.profile);
  const isPremium = hasPremiumAccess({
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  const passportCountryForVisaCall =
    profile?.citizenshipIso2 ||
    (profile?.citizenship ? toIso2CountryCode(profile.citizenship) : undefined) ||
    profile?.homeCountryIso2 ||
    (profile?.homeCountry ? toIso2CountryCode(profile.homeCountry) : undefined) ||
    undefined;
  const isNewUser = useUserStore((state) => state.isNewUser);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const tripDetectionSession = useTripDetectionSessionStore((state) => state.session);
  const clearTripDetectionSession = useTripDetectionSessionStore((state) => state.clearSession);
  const { showSuccess, showError, showInfo, showToastWithOptions } = useToast();
  const pendingQuickGmailSyncRef = useRef(false);
  const handleGmailSyncRef = useRef<(() => Promise<void>) | null>(null);
  const ratingTriggerSourceRef = useRef<'manual_trip_create' | 'smart_upload_trip_create' | null>(null);
  const [tripCreatedAnimationVisible, setTripCreatedAnimationVisible] = useState(false);
  const [tripCreatedAnimationMessage, setTripCreatedAnimationMessage] = useState<string | undefined>();


  const [trips, setTrips] = useState<Trip[]>([]);
  const tripsRef = useRef<Trip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const [isOnlineNow, setIsOnlineNow] = useState(networkMonitor.isOnline());
  // __DEV__ is false in Release / many device installs — also honor EXPO_PUBLIC_UI_DEBUG_LOGS so Metro / Safari Web Inspector shows lines when debugging.
  const logTripsTabLoading = (next: boolean, reason: string) => {
    if (__DEV__ || isUiDebugLoggingEnabled()) {
      console.log(`[TripsTab] tripsLoading=${next} (${reason})`);
    }
  };
  useLayoutEffect(() => {
    logTripsTabLoading(true, 'initial');
  }, []);
  useEffect(() => {
    tripsRef.current = trips;
  }, [trips]);
  useEffect(() => {
    const unsubscribe = networkMonitor.addListener(() => {
      setIsOnlineNow(networkMonitor.isOnline());
    });
    return unsubscribe;
  }, []);
  /**
   * Match server `getActiveTripCount`: owned trips plus active trips where the user is in `members`
   * or `memberIds` (see `functions/src/services/tripEntitlements.ts`).
   * While Firestore is still loading, merge with last-known cache so we don't flash "can create" before snapshots arrive.
   */
  const activeOwnedTripCountForLimit = useMemo(() => {
    const uid = firebaseUser?.uid;
    if (!uid) {
      return 0;
    }
    const ownedActive = countOwnedActiveTripsForFreeLimit(trips, uid);
    if (!tripsLoading) {
      return ownedActive;
    }
    const cached = getLastKnownTrips(uid)?.trips ?? [];
    const cachedOwnedActive = countOwnedActiveTripsForFreeLimit(cached, uid);
    return Math.max(ownedActive, cachedOwnedActive);
  }, [trips, tripsLoading, firebaseUser?.uid]);
  const canStartNewTrip = useMemo(
    () =>
      canCreateAnotherActiveTrip({
        isPremium: profile?.isPremium,
        planTier: profile?.planTier,
        entitlement: profile?.entitlement,
        activeTripCount: activeOwnedTripCountForLimit,
      }),
    [profile?.isPremium, profile?.planTier, profile?.entitlement, activeOwnedTripCountForLimit],
  );
  // Strictly live (non-cached) active owned trip count. Use this for irreversible UX actions
  // like presenting a premium gate in create flows.
  const activeOwnedTripCountLive = useMemo(() => {
    const uid = firebaseUser?.uid;
    if (!uid) {
      return 0;
    }
    return countOwnedActiveTripsForFreeLimit(trips, uid);
  }, [trips, firebaseUser?.uid]);
  const canStartNewTripLive = useMemo(
    () =>
      canCreateAnotherActiveTrip({
        isPremium: profile?.isPremium,
        planTier: profile?.planTier,
        entitlement: profile?.entitlement,
        activeTripCount: activeOwnedTripCountLive,
      }),
    [profile?.isPremium, profile?.planTier, profile?.entitlement, activeOwnedTripCountLive],
  );
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualFormPrefill, setManualFormPrefill] = useState<ManualTripFormPrefill | null>(null);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showEmailProviderSelection, setShowEmailProviderSelection] = useState(false);
  const showEmailProviderSelectionRef = useRef(showEmailProviderSelection);
  showEmailProviderSelectionRef.current = showEmailProviderSelection;
  const [selectedEmailProvider, setSelectedEmailProvider] = useState<InboxProviderId>('gmail');
  type InboxConnectionUiState = {
    syncing: boolean;
    operation: { kind: 'connect' | 'disconnect'; provider: InboxProviderId } | null;
    progress: { provider: InboxProviderId; phase: 'verifying' | 'detecting' } | null;
  };
  const [inboxConnectionUi, setInboxConnectionUi] = useState<InboxConnectionUiState>({
    syncing: false,
    operation: null,
    progress: null,
  });
  const gmailSyncing = inboxConnectionUi.syncing;
  const setGmailSyncing = (syncing: boolean) => {
    setInboxConnectionUi((prev) => ({ ...prev, syncing }));
  };
  const [gmailConnected, setGmailConnected] = useState<boolean | null>(null);
  const [connectedInboxProvider, setConnectedInboxProvider] = useState<InboxProviderId | null>(null);
  const inboxConnections = useInboxConnectionsStore((state) => state.connections);
  const inboxStatusLoading = useInboxConnectionsStore((state) => state.loading);
  const connectingProvider =
    inboxConnectionUi.operation?.kind === 'connect' ? inboxConnectionUi.operation.provider : null;
  const setConnectingProvider = (provider: InboxProviderId | null) => {
    setInboxConnectionUi((prev) => ({
      ...prev,
      operation: provider ? { kind: 'connect', provider } : prev.operation?.kind === 'connect' ? null : prev.operation,
    }));
  };
  const disconnectingProvider =
    inboxConnectionUi.operation?.kind === 'disconnect' ? inboxConnectionUi.operation.provider : null;
  const setDisconnectingProvider = (provider: InboxProviderId | null) => {
    setInboxConnectionUi((prev) => ({
      ...prev,
      operation: provider ? { kind: 'disconnect', provider } : prev.operation?.kind === 'disconnect' ? null : prev.operation,
    }));
  };
  const connectionProgress = inboxConnectionUi.progress;
  const setConnectionProgress = (
    progress: { provider: InboxProviderId; phase: 'verifying' | 'detecting' } | null,
  ) => {
    setInboxConnectionUi((prev) => ({ ...prev, progress }));
  };
  const [showEmailSyncStatus, setShowEmailSyncStatus] = useState(false);
  const [emailSyncTripId, setEmailSyncTripId] = useState<string | null>(null);
  const [emailSyncProvider, setEmailSyncProvider] = useState<InboxProviderId>('gmail');
  const [selectedTripForCollaboration, setSelectedTripForCollaboration] = useState<Trip | null>(null);
  const [selectedTripForActions, setSelectedTripForActions] = useState<Trip | null>(null);
  const [selectedTripForEdit, setSelectedTripForEdit] = useState<Trip | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const BUCKET_LIST_TITLE = 'Bucket List';
  const [filteredTripAlerts, setFilteredTripAlerts] = useState<TripAlert[]>([]);
  const [visibleTripIds, setVisibleTripIds] = useState<string[]>([]);
  const [heavyWidgetGateOpen, setHeavyWidgetGateOpen] = useState(false);
  const [footerWidgetsRequested, setFooterWidgetsRequested] = useState(false);
  const [travelRequirementsByTripId, setTravelRequirementsByTripId] = useState<Record<string, TravelRequirements | null>>({});
  const travelRequirementsCacheRef = useRef(
    new Map<string, { key: string; data: TravelRequirements | null; fetchedAt: number }>(),
  );
  const travelRequirementsInFlightRef = useRef(new Set<string>());
  /** Only run bulk visa prefetch when the set of trip ids or passport country actually changes (avoids 166+ calls from Firestore snapshot churn). */
  const lastTravelReqsEffectKeyRef = useRef<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<TripFilterType>('upcoming');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'destination'>('date');
  const [tripCardStates, setTripCardStates] = useState<Record<string, TripCardState>>({});

  // Template states
  const {
    showTemplateBrowser,
    showTemplateDetail,
    showTemplateCustomization,
    showTemplateBuilder,
    setActiveTemplateModal,
  } = useTemplateModalState();
  const [selectedTemplate, setSelectedTemplate] = useState<TripTemplate | null>(null);
  const [selectedTemplateTopicTag, setSelectedTemplateTopicTag] = useState<string | undefined>(undefined);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [pendingTemplateTripId, setPendingTemplateTripId] = useState<string | null>(null);
  const [templateBuilderSourceTripId, setTemplateBuilderSourceTripId] = useState<string | undefined>(undefined);
  const [templateBrowserInitialTopic, setTemplateBrowserInitialTopic] = useState<string | undefined>(undefined);
  const [templateDeeplinkRef, setTemplateDeeplinkRef] = useState<string | undefined>(undefined);
  const [templateDeeplinkUtmSource, setTemplateDeeplinkUtmSource] = useState<string | undefined>(undefined);
  const templateDeepLinkHandledRef = useRef(false);
  const routeParams = useGlobalSearchParams<{
    openTemplates?: string | string[];
    templateTopic?: string | string[];
    templateId?: string | string[];
    ref?: string | string[];
    utm_source?: string | string[];
  }>();
  const [cleanupInProgress, setCleanupInProgress] = useState(false);
  const [emailSyncResults, setEmailSyncResults] = useState<{
    tripId: string;
    provider: InboxProviderId;
    bookings: ExtractedBooking[];
    timestamp: number;
  } | null>(null);
  const [showEmailSyncResultsModal, setShowEmailSyncResultsModal] = useState(false);
  const [emailSyncResultsData, setEmailSyncResultsData] = useState<{
    bookings: ExtractedBooking[];
    bookingsForOtherTrips: Array<{ booking: ExtractedBooking; tripId: string; score: number }>;
    provider: InboxProviderId;
  } | null>(null);
  const openPremiumGateWithEmailSyncClosed = useCallback(
    (payload: PremiumGatePayload) => {
      setShowEmailSyncStatus(false);
      setShowEmailSyncResultsModal(false);
      InteractionManager.runAfterInteractions(() => {
        openPremiumGate(payload);
      });
    },
    [openPremiumGate],
  );
  const hasRefreshedSeoulImage = useRef(false);
  const [smartUploadVisible, setSmartUploadVisible] = useState(false);
  const [showNewTripOptions, setShowNewTripOptions] = useState(false);
  const [selectedSavedRecommendation, setSelectedSavedRecommendation] = useState<SavedRecommendation | null>(null);
  const [showSavedRecommendationDetail, setShowSavedRecommendationDetail] = useState(false);
  const [oauthListenerActive, setOauthListenerActive] = useState(false);
  const oauthUserInitiatedRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      setOauthListenerActive(true);
      return () => {
        setOauthListenerActive(false);
      };
    }, []),
  );

  const detectedTripsPresentationTokenRef = useRef(0);
  const detectedTripsPresentationCancelRef = useRef<(() => void) | null>(null);
  const pendingGoAfterProviderModalRef = useRef<{ presentationToken: number; run: () => void } | null>(null);
  const pendingProviderModalFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      detectedTripsPresentationTokenRef.current += 1;
      detectedTripsPresentationCancelRef.current?.();
      detectedTripsPresentationCancelRef.current = null;
      if (pendingProviderModalFallbackRef.current) {
        clearTimeout(pendingProviderModalFallbackRef.current);
        pendingProviderModalFallbackRef.current = null;
      }
      pendingGoAfterProviderModalRef.current = null;
    };
  }, []);

  const presentDetectedTripsSafely = useCallback((provider: InboxProviderId) => {
    detectedTripsPresentationTokenRef.current += 1;
    const presentationToken = detectedTripsPresentationTokenRef.current;
    detectedTripsPresentationCancelRef.current?.();
    detectedTripsPresentationCancelRef.current = null;

    if (pendingProviderModalFallbackRef.current) {
      clearTimeout(pendingProviderModalFallbackRef.current);
      pendingProviderModalFallbackRef.current = null;
    }
    pendingGoAfterProviderModalRef.current = null;

    const go = () => {
      if (presentationToken !== detectedTripsPresentationTokenRef.current) return;
      detectedTripsPresentationCancelRef.current = null;
      completeOAuthWebSession();
      setSelectedEmailProvider(provider);
      setConnectionProgress(null);
      clearTripDetectionSession();
      router.push({ pathname: '/trip-detection', params: { provider } });
    };

    if (Platform.OS === 'web') {
      setShowEmailProviderSelection(false);
      // Web has no native auth-session presenter to dismiss. Navigate immediately; the delayed
      // native scheduling path can leave Safari on the Trips tab with only the "Scanning..." toast.
      setTimeout(() => {
        go();
        setTimeout(() => {
          if (typeof window === 'undefined') return;
          if (window.location.pathname === '/trip-detection') return;
          window.location.assign(`/trip-detection?provider=${encodeURIComponent(provider)}`);
        }, 250);
      }, 0);
      return;
    }

    if (showEmailProviderSelectionRef.current) {
      pendingGoAfterProviderModalRef.current = { presentationToken, run: go };
      setShowEmailProviderSelection(false);
      const fallbackMs = Platform.OS === 'ios' ? 1400 : 900;
      pendingProviderModalFallbackRef.current = setTimeout(() => {
        pendingProviderModalFallbackRef.current = null;
        const still = pendingGoAfterProviderModalRef.current;
        if (!still || still.presentationToken !== presentationToken) return;
        pendingGoAfterProviderModalRef.current = null;
        detectedTripsPresentationCancelRef.current?.();
        detectedTripsPresentationCancelRef.current = schedulePresentAfterAuthSessionDismiss(still.run);
      }, fallbackMs);
    } else {
      detectedTripsPresentationCancelRef.current = schedulePresentAfterAuthSessionDismiss(go);
    }
  }, [clearTripDetectionSession, router]);

  const onEmailProviderModalNativeDismiss = useCallback(() => {
    if (pendingProviderModalFallbackRef.current) {
      clearTimeout(pendingProviderModalFallbackRef.current);
      pendingProviderModalFallbackRef.current = null;
    }
    const pending = pendingGoAfterProviderModalRef.current;
    pendingGoAfterProviderModalRef.current = null;
    if (!pending) return;
    detectedTripsPresentationCancelRef.current?.();
    detectedTripsPresentationCancelRef.current = schedulePresentAfterAuthSessionDismiss(pending.run);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get('oauth');
    const providerParam = params.get('provider');
    const provider = providerParam === 'gmail' || providerParam === 'outlook' ? providerParam : null;

    if (oauthStatus === 'success' && provider) {
      // intentional: best-effort AsyncStorage write; a failure here just means the
      // auto-resume on next focus won't fire — the user can still tap Connect again.
      void setInboxOauthSuccess(provider).catch(() => {});
      params.delete('oauth');
      params.delete('provider');
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
      window.history.replaceState({}, '', next);
    }
  }, []);
  const landmarkBackfillInFlightRef = useRef<Set<string>>(new Set());
  const landmarkBackfillJobIdRef = useRef(0);
  
  // Tutorial target refs
  const newTripButtonRef = useRef<View>(null);
  const aiAssistantButtonRef = useRef<View>(null);
  const emailSyncButtonRef = useRef<View>(null);
  const templatesWidgetRef = useRef<View>(null);

  // Register tutorial targets
  useEffect(() => {
    // Register ref objects (not .current). The tutorial can measure once the element mounts.
    registerTutorialTarget('new-trip-button', newTripButtonRef);
    registerTutorialTarget('ai-assistant-button', aiAssistantButtonRef);
    registerTutorialTarget('email-sync-button', emailSyncButtonRef);
    registerTutorialTarget('templates-widget', templatesWidgetRef);

    return () => {
      unregisterTutorialTarget('new-trip-button');
      unregisterTutorialTarget('ai-assistant-button');
      unregisterTutorialTarget('email-sync-button');
      unregisterTutorialTarget('templates-widget');
    };
  }, []);

  // --- Perf instrumentation (DEV-only, opt-in) ---
  const perfEnabled = __DEV__ && process.env.EXPO_PUBLIC_PERF_DEBUG === 'true';
  const useFlashList = process.env.EXPO_PUBLIC_USE_FLASHLIST === 'true';
  const FlashList = useMemo(() => {
    if (!useFlashList) return null;
    try {
      // Optional dependency: if not installed, gracefully fall back to FlatList.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const mod = require('@shopify/flash-list');
      return mod?.FlashList ?? null;
    } catch {
      return null;
    }
  }, [useFlashList]);
  const screenMountedAtRef = useRef<number>(Date.now());
  const ttiLoggedRef = useRef(false);
  /** One non-fatal Crashlytics report per trips subscription (resets when uid/effect re-runs). */
  const tripsWatchdogNonFatalSentRef = useRef(false);
  const onTripsProfilerRender = useCallback<React.ProfilerOnRenderCallback>(
    (id, phase, actualDuration, baseDuration, startTime, commitTime) => {
      if (!perfEnabled) return;
      // Keep this lightweight; only warn on slow commits.
      const payload = { id, phase, actualDuration, baseDuration, startTime, commitTime };
      if (actualDuration > 24) {
        // ~1.5 frames at 60fps
        console.warn('[Perf][TripsScreen] Slow commit', {
          phase,
          actualDuration,
          baseDuration,
        });
      } else {
        // eslint-disable-next-line no-console
        console.log('[Perf][TripsScreen] commit', { phase, actualDuration });
      }
    },
    [perfEnabled],
  );

  const handleBucketListItemPress = useCallback(
    async (recommendation: any) => {
      // SavedRecommendation is a superset of TripRecommendation and may include template metadata.
      const isTemplate = recommendation?.source === 'template' && typeof recommendation?.templateId === 'string';
      if (isTemplate) {
        if (!canStartNewTrip) {
          openPremiumGateWithEmailSyncClosed({
            feature: 'trip_limit',
            title: '1 active trip on Free',
            message:
              'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
            source: 'feature_gate',
          });
          return;
        }
        try {
          const template = await getTemplateById(recommendation.templateId);
          if (!template) {
            Alert.alert('Template not found', 'This template may have been removed. Please try another one.');
            return;
          }
          setTemplateEntryPoint('widget');
          setSelectedTemplate(template);
          setActiveTemplateModal('detail');
          return;
        } catch (err) {
          console.error('[TripsScreen] Failed to open template from saved recommendation:', err);
          Alert.alert('Unable to open template', 'Please try again.');
          return;
        }
      }
      // AI saved recommendation → show full details modal (summary, cost breakdown, why this matches).
      setSelectedSavedRecommendation(recommendation as SavedRecommendation);
      setShowSavedRecommendationDetail(true);
    },
    [setSelectedTemplate, setActiveTemplateModal, canStartNewTrip, openPremiumGateWithEmailSyncClosed],
  );

  const tripById = useMemo(() => {
    const map = new Map<string, Trip>();
    trips.forEach((trip) => {
      map.set(trip.id, trip);
    });
    return map;
  }, [trips]);
  const visibleTripIdsKeyRef = useRef('');
  const viewabilityConfigRef = useRef({
    itemVisiblePercentThreshold: 35,
    minimumViewTime: 120,
  });
  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: Array<ViewToken> }) => {
      const nextVisibleTripIds = Array.from(
        new Set(
          viewableItems.flatMap((token) => {
            if (!token.isViewable) {
              return [];
            }
            const item = token.item as { type?: string; trip?: Trip } | undefined;
            if (item?.type !== 'trip' || !item.trip?.id) {
              return [];
            }
            return [item.trip.id];
          }),
        ),
      );
      const nextKey = nextVisibleTripIds.join('|');
      if (nextKey === visibleTripIdsKeyRef.current) {
        return;
      }
      visibleTripIdsKeyRef.current = nextKey;
      setVisibleTripIds(nextVisibleTripIds);
    },
  );
  const handleFooterWidgetsNearViewport = useCallback(() => {
    setFooterWidgetsRequested(true);
  }, []);

  useEffect(() => {
    setHeavyWidgetGateOpen(false);
    setFooterWidgetsRequested(false);

    if (Platform.OS === 'web') {
      // Web can keep "interactions" active for long stretches; open deferred widgets quickly.
      const webTimer = setTimeout(() => {
        setHeavyWidgetGateOpen(true);
      }, 120);
      return () => {
        clearTimeout(webTimer);
      };
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const interactionHandle = InteractionManager.runAfterInteractions(() => {
      timer = setTimeout(() => {
        if (!cancelled) {
          setHeavyWidgetGateOpen(true);
        }
      }, HEAVY_WIDGET_DEFER_MS);
    });

    return () => {
      cancelled = true;
      interactionHandle?.cancel?.();
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [firebaseUser?.uid]);

  const handleDismissEmailSyncResults = useCallback(() => {
    setEmailSyncResults(null);
  }, []);

  const handleViewSyncedTrip = useCallback(() => {
    if (emailSyncResults?.tripId) {
      router.push({
        pathname: '/(tabs)/plan',
        params: { tripId: emailSyncResults.tripId },
      });
      setEmailSyncResults(null);
    }
  }, [emailSyncResults?.tripId, router]);

  const formatBookingDate = useCallback((booking: ExtractedBooking): string | null => {
    const date =
      booking.checkInDate ||
      booking.departureDate ||
      booking.arrivalDate ||
      booking.checkOutDate ||
      booking.rawEmailDate;
    if (!date) return null;
    const asDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(asDate.getTime())) return null;
    return asDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);


  // Subscribe to trips updates
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTrips([]);
      logTripsTabLoading(false, 'no-uid');
      setTripsLoading(false);
      return;
    }

    let cancelled = false;
    const uid = firebaseUser.uid;
    const lastKnownTrips = getLastKnownTrips(uid)?.trips ?? [];
    if (lastKnownTrips.length > 0) {
      setTrips(lastKnownTrips);
      logTripsTabLoading(false, 'last-known');
      setTripsLoading(false);
    } else {
      logTripsTabLoading(true, 'subscribe-start');
      setTripsLoading(true);
      void getCachedTrips(uid)
        .then((cached) => {
          if (cancelled || !cached?.length) return;
          setTrips(cached);
          logTripsTabLoading(false, 'disk-cache');
          setTripsLoading(false);
        })
        .catch((err) => {
          console.warn('[TripsScreen] getCachedTrips early hydrate failed', err);
        });
      // Full list + listener: `subscribeToUserTrips` (continues cache hydrate + poll or realtime).
    }

    tripsWatchdogNonFatalSentRef.current = false;
    const watchdogMs = 3500;
    const watchdog = setTimeout(() => {
      if (cancelled) return;
      setTripsLoading((stillLoading) => {
        if (stillLoading) {
          console.warn(
            `[TripsScreen] Trips list still loading after ${watchdogMs}ms; clearing loading state. Check Firestore rules and connectivity.`,
          );
          addBreadcrumb('Trips tab 8s loading watchdog cleared', 'trips.ui', 'warning');
          if (!tripsWatchdogNonFatalSentRef.current) {
            tripsWatchdogNonFatalSentRef.current = true;
            void recordCrashlyticsNonFatal(
              new Error(`TripsTabLoadingWatchdog: still loading after ${watchdogMs}ms`),
              { screen: 'trips', uid: firebaseUser.uid },
            );
          }
          logTripsTabLoading(false, 'watchdog');
        }
        return false;
      });
    }, watchdogMs);

    const unsubscribe = subscribeToUserTrips(
      uid,
      (userTrips) => {
        clearTimeout(watchdog);
        try {
          // Keep subscription callback lightweight to avoid UI jank. Landmark image backfill is handled
          // in a deferred, concurrency-limited effect.
          setTrips((prev) => {
            if (
              networkMonitor.shouldUseOfflineDataFallback() &&
              prev.length > 0 &&
              userTrips.length === 0
            ) {
              return prev;
            }
            return userTrips;
          });
        } finally {
          // Always leave loading — a thrown setState updater must not stick the tab on skeletons.
          logTripsTabLoading(false, 'snapshot');
          setTripsLoading(false);
        }
      },
      {
        onError: (err) => {
          clearTimeout(watchdog);
          console.warn('[TripsScreen] subscribeToUserTrips error', err);
          const code = String((err as { code?: string })?.code ?? '');
          addBreadcrumb('TripsScreen subscribeToUserTrips onError', 'trips.firestore', 'error', {
            code,
          });
          const asError = err instanceof Error ? err : new Error(String(err));
          void recordCrashlyticsNonFatal(asError, {
            screen: 'trips',
            source: 'subscribeToUserTrips',
            code,
            uid: firebaseUser.uid,
          });
          logTripsTabLoading(false, 'subscribe-error');
          setTripsLoading(false);
        },
      },
    );

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      unsubscribe();
      // If subscribeToUserTrips was torn down mid-hydration, terminal emit may not run; never
      // leave the tab stuck on skeletons until the next focus cycle.
      logTripsTabLoading(false, 'unmount-cleanup');
      setTripsLoading(false);
    };
  }, [firebaseUser?.uid]);

  // Re-fetch trip list from server when the tab gains focus so date edits are not overwritten
  // by stale disk cache / lagging poll snapshots (Plan tab already does this per selected trip).
  useFocusEffect(
    useCallback(() => {
      const uid = firebaseUser?.uid;
      if (!uid) return;
      void refreshTripListFromServer(uid)
        .then((nextTrips) => {
          setTrips(nextTrips);
        })
        .catch(() => {});
    }, [firebaseUser?.uid]),
  );

  // Defer landmark image backfill (avoid N network calls + Firestore writes during subscription processing)
  useEffect(() => {
    if (!firebaseUser?.uid) return;
    if (tripsLoading) return;
    if (trips.length === 0) return;

    // Only backfill a bounded set per run to avoid long tasks.
    const candidates = trips
      .map((trip) => ({
        trip,
        destination: getTripLandmarkDestination(trip) || trip.destination || '',
      }))
      .filter(({ trip, destination }) => Boolean(trip.id && destination && !trip.landmarkImage))
      .filter(({ trip }) => !landmarkBackfillInFlightRef.current.has(trip.id))
      .slice(0, 12);

    if (candidates.length === 0) return;

    const jobId = ++landmarkBackfillJobIdRef.current;
    let cancelled = false;

    const PLACEHOLDER_PREFIX = 'https://placehold.co/';
    const run = async () => {
      const concurrency = 3;
      const queue = [...candidates];

      const worker = async () => {
        while (queue.length && !cancelled && jobId === landmarkBackfillJobIdRef.current) {
          const next = queue.shift();
          if (!next) return;
          const { trip, destination } = next;
          if (!trip.id) continue;
          if (landmarkBackfillInFlightRef.current.has(trip.id)) continue;

          landmarkBackfillInFlightRef.current.add(trip.id);
          try {
            const result = await fetchAndCacheLandmarkImage(destination);
            if (!result?.url) continue;
            if (result.url.startsWith(PLACEHOLDER_PREFIX)) continue; // never write placeholders
            if (cancelled || jobId !== landmarkBackfillJobIdRef.current) return;

            // Update local list immediately to reduce downstream image fetches in row components.
            setTrips((prev) => {
              if (cancelled || jobId !== landmarkBackfillJobIdRef.current) return prev;
              const idx = prev.findIndex((t) => t.id === trip.id);
              if (idx < 0) return prev;
              const existing = prev[idx];
              if (existing.landmarkImage) return prev;
              const updated: Trip = {
                ...existing,
                landmarkImage: result.url,
                landmarkImageCredit: result.creditText ?? existing.landmarkImageCredit ?? null,
                landmarkImageCreditUrl: result.creditUrl ?? existing.landmarkImageCreditUrl ?? null,
              };
              const copy = [...prev];
              copy[idx] = updated;
              return copy;
            });

            // Best-effort: persist onto the trip doc (async, but bounded and delayed).
            updateTripLandmarkImage(trip.id, destination, result).catch((error) => {
              const message = error instanceof Error ? error.message : String(error);
              console.warn('[TripsScreen] Failed to persist landmark image', { message });
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.warn('[TripsScreen] Landmark image backfill failed', { message });
          } finally {
            landmarkBackfillInFlightRef.current.delete(trip.id);
          }
        }
      };

      await Promise.all(Array.from({ length: concurrency }, () => worker()));
    };

    const handle = InteractionManager.runAfterInteractions(() => {
      run().catch((error) => {
        console.warn('[TripsScreen] Landmark backfill run failed', error);
      });
    });

    return () => {
      cancelled = true;
      // Cancel scheduled callback if it hasn't run yet.
      handle?.cancel?.();
    };
  }, [firebaseUser?.uid, tripsLoading, trips]);

  // Best-effort "Trips screen TTI" measure: time from mount to first stable list state.
  useEffect(() => {
    if (!perfEnabled) return;
    if (ttiLoggedRef.current) return;
    if (firebaseUser?.uid && !tripsLoading) {
      ttiLoggedRef.current = true;
      const ms = Date.now() - screenMountedAtRef.current;
      // eslint-disable-next-line no-console
      console.log('[Perf][TripsScreen] TTI-ish (mount→tripsLoaded)', { ms, tripsCount: trips.length });
    }
  }, [perfEnabled, firebaseUser?.uid, tripsLoading, trips.length]);

  // Check Gmail connection status
  useEffect(() => {
    if (firebaseUser?.uid) {
      checkGmailConnection().then(setGmailConnected).catch(() => setGmailConnected(false));
    }
  }, [firebaseUser?.uid]);

  // Mirror global inbox snapshot to "preferred" Quick Actions provider: keep prior if still valid.
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setConnectedInboxProvider(null);
      return;
    }
    const order: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
    const fromSnapshot = order.find((p) => inboxConnections[p]) ?? null;
    setConnectedInboxProvider((prev) => {
      if (prev && inboxConnections[prev]) return prev;
      return fromSnapshot;
    });
  }, [firebaseUser?.uid, inboxConnections]);

  const handleOauthConnected = useCallback(
    async (provider: InboxProviderId) => {
      setConnectedInboxProvider(provider);
      setSelectedEmailProvider(provider);
      setShowEmailProviderSelection(false);
      if (firebaseUser?.uid) {
        setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
      }

      if (provider === 'gmail' && pendingQuickGmailSyncRef.current) {
        pendingQuickGmailSyncRef.current = false;
        oauthUserInitiatedRef.current = false;
        showInfo('Connected, starting sync...');
        void handleGmailSyncRef.current?.();
        return;
      }

      const wasUserInitiated = oauthUserInitiatedRef.current;
      oauthUserInitiatedRef.current = false;

      if (wasUserInitiated) {
        showInfo('Connected. Detecting trips from your inbox...');
        presentDetectedTripsSafely(provider);
      } else {
        showInfo('Email connected.');
      }
    },
    [firebaseUser?.uid, showInfo, presentDetectedTripsSafely],
  );

  const handleOauthResumePollFinishedWithoutConnect = useCallback(
    (_provider: InboxProviderId) => {
      oauthUserInitiatedRef.current = false;
      setInboxConnectionUi((prev) => {
        if (prev.progress) {
          queueMicrotask(() => {
            showInfo(
              'Still confirming your email connection. Tap Connect Email again in a few seconds if needed.',
            );
          });
        }
        return { ...prev, progress: null };
      });
      // intentional: best-effort AsyncStorage cleanup; if it fails the stale markers
      // are harmless — they'll be overwritten on the next OAuth attempt.
      void clearInboxOauthMarkers().catch(() => {});
    },
    [showInfo],
  );

  const checkInboxStatusFresh = useCallback(
    (p: InboxProviderId) => checkInboxStatus(p, { bypassCache: true }),
    [],
  );

  useInboxOauthResume({
    userId: firebaseUser?.uid,
    enabled: oauthListenerActive,
    checkStatus: checkInboxStatusFresh,
    onConnected: handleOauthConnected,
    onResumePollFinishedWithoutConnect: handleOauthResumePollFinishedWithoutConnect,
  });

  useInboxOauthPostMessage({
    userId: firebaseUser?.uid,
    enabled: oauthListenerActive,
    checkStatus: checkInboxStatusFresh,
    onConnected: handleOauthConnected,
  });

  // Subscribe to trip card states for dismissal/minimize preferences
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTripCardStates({});
      return;
    }

    const unsubscribe = subscribeToTripCardStates(firebaseUser.uid, (states) => {
      const map = states.reduce<Record<string, TripCardState>>((acc, state) => {
        acc[state.tripId] = state;
        return acc;
      }, {});
      setTripCardStates(map);
    });

    return () => {
      unsubscribe();
    };
  }, [firebaseUser?.uid]);
  const runGmailQuickSync = useCallback(async () => {
    // For quick actions sync, use a dummy tripId for matching purposes
    // We'll use autoStore: false to get bookings for user confirmation
    // If no trips exist, we'll use a placeholder - the backend will still match against existing trips
    const tripId = trips.length > 0 ? trips[0].id : 'quick-sync'; // Use first trip as reference, or placeholder if no trips
    showInfo('Scanning your Gmail for booking confirmations...');

    // Call sync with autoStore: false to get bookings for user confirmation
    const syncResult = await syncInboxBookings(tripId, 'gmail', { autoStore: false });

    if (syncResult.status === 'auth') {
      showError('Please connect your Gmail account first.');
      return;
    }

    if (syncResult.status === 'error') {
      showError(syncResult.message || 'Failed to sync Gmail. Please try again.');
      return;
    }

    if (syncResult.status === 'ok') {
      const unmatchedBookings = syncResult.unmatchedBookings || [];
      const bookingsForOtherTrips = syncResult.bookingsForOtherTrips || [];

      // If no bookings found, show message
      if (unmatchedBookings.length === 0 && bookingsForOtherTrips.length === 0) {
        showInfo('No new bookings found in your Gmail.');
        return;
      }

      // Show results modal for user to choose action
      setEmailSyncResultsData({
        bookings: unmatchedBookings,
        bookingsForOtherTrips,
        provider: 'gmail',
      });
      setShowEmailSyncResultsModal(true);
    }
  }, [trips, showError, showInfo]);

  const handleGmailSync = useCallback(async () => {
    if (!firebaseUser?.uid) {
      showError('Please sign in to sync Gmail.');
      return;
    }

    oauthUserInitiatedRef.current = true;
    setGmailSyncing(true);
    try {
      // Check if Gmail is connected
      const connected = await checkGmailConnection();
      
      if (!connected) {
        // Initiate OAuth flow - simplified: no confirmation dialog
        showInfo('Connecting to Gmail...');
        const authUrl = await initiateGmailAuth();
        if (authUrl) {
          try {
            pendingQuickGmailSyncRef.current = true;
            await setInboxOauthPending('gmail').catch(() => {});
            // On web, use popup first to preserve app state and receive postMessage callback.
            if (Platform.OS === 'web') {
              let popup: Window | null = null;
              try {
                popup = window.open('about:blank', '_blank');
              } catch {
                popup = null;
              }

              if (popup) {
                try {
                  popup.location.href = authUrl;
                } catch {
                  window.location.href = authUrl;
                }
              } else {
                // Popup blocked - fallback to same tab.
                window.location.href = authUrl;
              }
            } else {
              // On mobile, open directly without confirmation
              const redirectUrl = Linking.createURL('gmail-callback');
              await WebBrowser.openAuthSessionAsync(authUrl, redirectUrl);
              completeOAuthWebSession();
            }
            setConnectionProgress({ provider: 'gmail', phase: 'verifying' });
            showInfo('Please complete authentication in your browser.');
          } catch (error) {
            pendingQuickGmailSyncRef.current = false;
            setConnectionProgress(null);
            console.error('Failed to open OAuth URL:', error);
            showError('Could not open authentication page.');
            setGmailSyncing(false);
          }
        } else {
          pendingQuickGmailSyncRef.current = false;
          showError('Could not initiate Gmail authentication. Please try again later.');
          setGmailSyncing(false);
        }
        return;
      }
      pendingQuickGmailSyncRef.current = false;
      showInfo('Connected, starting sync...');
      await runGmailQuickSync();
      return;
    } catch (error) {
      console.error('Gmail sync error:', error);
      const rateLimited = inboxCallableRateLimitUserMessage(error);
      if (rateLimited) {
        showError(rateLimited);
        return;
      }
      let errorMessage = 'We couldn’t finish Gmail sync. Please try again.';
      if (error instanceof Error) {
        if (
          error.message.includes('CORS') ||
          error.message.includes('internal') ||
          (error as any).code === 'functions/internal'
        ) {
          errorMessage = 'Unable to connect to the server. Check your connection and try again.';
        } else {
          errorMessage = humanizeAsyncError(error, errorMessage);
        }
      }
      showError(errorMessage);
    } finally {
      setGmailSyncing(false);
    }
  }, [firebaseUser?.uid, showError, showInfo, runGmailQuickSync]);

  useEffect(() => {
    handleGmailSyncRef.current = handleGmailSync;
  }, [handleGmailSync]);

  const updateTripCardVisibility = useCallback(
    async (
      tripId: string,
      visibility: TripCardVisibility,
      reason: 'user' | 'auto' | 'system' = 'user',
    ) => {
      if (!firebaseUser?.uid) {
        return;
      }

      const trip = tripById.get(tripId);

      let previousSnap: TripCardState | undefined;
      setTripCardStates((prev) => {
        previousSnap = prev[tripId];
        return {
          ...prev,
          [tripId]: {
            ...(previousSnap ?? {
              tripId,
              visibility,
            }),
            tripId,
            visibility,
          },
        };
      });

      try {
        await setTripCardVisibility(firebaseUser.uid, tripId, visibility, {
          reason,
          tripEndDate: trip?.endDate ?? null,
        });
      } catch (error) {
        console.error('Failed to update trip card visibility', error);
        setTripCardStates((prev) => {
          const next = { ...prev };
          if (previousSnap) {
            next[tripId] = previousSnap;
          } else {
            delete next[tripId];
          }
          return next;
        });
        showError('Unable to update trip card. Please try again.');
      }
    },
    [firebaseUser?.uid, tripById, showError],
  );

  const handleDismissActiveTripCard = useCallback(
    (tripId: string) => {
      void logAnalyticsEvent('day_briefing_dismiss', {
        tripId,
        surface: 'active_trip_card',
      });
      updateTripCardVisibility(tripId, 'hidden', 'user');
    },
    [updateTripCardVisibility],
  );

  const handleMinimizeActiveTripCard = useCallback(
    (tripId: string) => {
      void logAnalyticsEvent('day_briefing_dismiss', {
        tripId,
        surface: 'active_trip_card',
      });
      updateTripCardVisibility(tripId, 'minimized');
    },
    [updateTripCardVisibility],
  );

  const handleExpandActiveTripCard = useCallback(
    (tripId: string) => {
      void logAnalyticsEvent('day_briefing_expand', {
        tripId,
        surface: 'minimized_tray',
      });
      updateTripCardVisibility(tripId, 'expanded', 'system');
    },
    [updateTripCardVisibility],
  );

  // Filter and sort trips
  const activeTrips = useMemo(() => {
    return trips.filter(trip => !trip.isArchived);
  }, [trips]);

  const cardEligibleTrips = useMemo(() => {
    const now = Date.now();
    return activeTrips.filter((trip) => {
      if (trip.startDate.getTime() > now) {
        return false;
      }
      const completedAgo = now - trip.endDate.getTime();
      if (completedAgo > ACTIVE_TRIP_CARD_GRACE_PERIOD_MS) {
        return false;
      }
      const state = tripCardStates[trip.id];
      return state?.visibility !== 'hidden';
    });
  }, [activeTrips, tripCardStates]);

  useEffect(() => {
    if (!firebaseUser?.uid) {
      return;
    }

    const now = Date.now();

    activeTrips.forEach((trip) => {
      const completedAgo = now - trip.endDate.getTime();
      if (completedAgo <= ACTIVE_TRIP_CARD_GRACE_PERIOD_MS) {
        return;
      }
      const state = tripCardStates[trip.id];
      if (state?.autoHiddenAt || state?.visibility === 'hidden') {
        return;
      }
      setTripCardVisibility(firebaseUser.uid, trip.id, 'hidden', {
        reason: 'auto',
        tripEndDate: trip.endDate,
      }).catch((error) => {
        console.error('Failed to auto-hide trip card state', {
          tripId: trip.id,
          error,
        });
      });
    });
  }, [activeTrips, firebaseUser?.uid, tripCardStates]);

  const archivedTrips = useMemo(() => {
    return trips.filter(trip => trip.isArchived);
  }, [trips]);

  const hiddenActiveTrip = useMemo(() => {
    const now = Date.now();
    return activeTrips.find((trip) => {
      if (trip.startDate.getTime() > now) return false;
      const completedAgo = now - trip.endDate.getTime();
      if (completedAgo > ACTIVE_TRIP_CARD_GRACE_PERIOD_MS) return false;
      const state = tripCardStates[trip.id];
      return state?.visibility === 'hidden';
    }) ?? null;
  }, [activeTrips, tripCardStates]);

  const { highlights: itineraryHighlights } = useUpcomingItineraryHighlights(activeTrips);

  useAppleTripSurfaceTripsSync({
    trips,
    userId: firebaseUser?.uid,
    enabled: !tripsLoading && Boolean(firebaseUser?.uid),
  });

  const featuredTripHighlight = useMemo(() => {
    if (cardEligibleTrips.length === 0) {
      return null;
    }
    const highlighted = cardEligibleTrips.find((trip) => itineraryHighlights[trip.id]);
    if (highlighted) {
      return {
        trip: highlighted,
        item: itineraryHighlights[highlighted.id] ?? null,
      };
    }
    return {
      trip: cardEligibleTrips[0],
      item: itineraryHighlights[cardEligibleTrips[0].id] ?? null,
    };
  }, [cardEligibleTrips, itineraryHighlights]);

  const featuredTripId = featuredTripHighlight?.trip.id;
  const featuredTripCardState: TripCardState | undefined = featuredTripId ? tripCardStates[featuredTripId] : undefined;
  const featuredTripCardVisibility: TripCardVisibility = featuredTripCardState?.visibility ?? 'expanded';
  const shouldShowHeaderCard =
    !supportsBottomSheet &&
    featuredTripHighlight &&
    featuredTripCardVisibility !== 'hidden' &&
    featuredTripCardVisibility !== 'minimized';
  const shouldShowMinimizedHeaderChip =
    !supportsBottomSheet && featuredTripHighlight && featuredTripCardVisibility === 'minimized';
  const shouldShowBottomSheet =
    supportsBottomSheet &&
    featuredTripHighlight &&
    featuredTripCardVisibility !== 'hidden' &&
    featuredTripCardVisibility !== 'minimized';

  // Keep list padding modest; tab bar already handles safe area.
  const baseListPadding = theme.spacing(2);
  const sheetPadding = shouldShowBottomSheet ? 240 : 0; // reserve space only when sheet is visible


  // Fetch bookings for featured trip to show flight details
  const [featuredTripBookings, setFeaturedTripBookings] = useState<Booking[]>([]);
  
  useEffect(() => {
    if (!featuredTripHighlight?.trip?.id) {
      setFeaturedTripBookings([]);
      return;
    }

    const unsubscribe = subscribeToTripBookings(featuredTripHighlight.trip.id, (bookings) => {
      setFeaturedTripBookings(bookings);
    });

    return () => {
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors
        }
      }
    };
  }, [featuredTripHighlight?.trip?.id]);

  // Match booking to nextItem
  const matchedBooking = useMemo(() => {
    if (!featuredTripHighlight?.item || featuredTripBookings.length === 0) {
      return null;
    }
    
    const nextItem = featuredTripHighlight.item;
    
    // First try to match by bookingId
    if (nextItem.bookingId) {
      const booking = featuredTripBookings.find(b => b.id === nextItem.bookingId);
      if (booking) return booking;
    }
    
    // Then try to match by confirmation number from bookingSnapshot
    if (nextItem.bookingSnapshot?.confirmationNumber) {
      const booking = featuredTripBookings.find(
        b => b.confirmationNumber === nextItem.bookingSnapshot?.confirmationNumber
      );
      if (booking) return booking;
    }
    
    // Finally, try to match flight bookings by date/time if it's a flight item
    if (nextItem.category === 'flight' && nextItem.startDate) {
      const booking = featuredTripBookings.find(b => {
        if (b.type !== 'flight' || !b.departureDate) return false;
        const bookingDate = new Date(b.departureDate);
        const itemDate = new Date(nextItem.startDate);
        // Match if dates are within 24 hours
        return Math.abs(bookingDate.getTime() - itemDate.getTime()) < 24 * 60 * 60 * 1000;
      });
      if (booking) return booking;
    }
    
    return null;
  }, [featuredTripHighlight?.item, featuredTripBookings]);

  const hasActiveTrips = activeTrips.length > 0;
  const hasArchivedTrips = archivedTrips.length > 0;

  // Group trips by status
  const completedTrips = useMemo(() => {
    return activeTrips
      .filter((trip) => isTripCompleted(trip))
      .sort((a, b) => b.endDate.getTime() - a.endDate.getTime());
  }, [activeTrips]);

  const upcomingTrips = useMemo(() => {
    const now = new Date();
    // Include trips that are not completed - this includes both upcoming (startDate > now) 
    // and ongoing (startDate <= now but endDate > now) trips
    return activeTrips.filter((trip) => !isTripCompleted(trip));
  }, [activeTrips]);

  /** Avoid "Upcoming (0)" etc. until we have either a hydrated list or a confirmed empty load. */
  const suppressTripFilterCounts = tripsLoading && trips.length === 0;

  const filteredTrips = useMemo(() => {
    let filtered: Trip[];
    
    // Apply filter
    switch (activeFilter) {
      case 'upcoming':
        filtered = upcomingTrips;
        break;
      case 'completed':
        filtered = completedTrips;
        break;
      case 'archived':
        filtered = archivedTrips;
        break;
      default:
        filtered = upcomingTrips;
    }
    
    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((trip) =>
        getTripDestinationSearchText(trip).includes(query)
      );
    }
    
    const sortable = [...filtered];
    if (activeFilter === 'completed') {
      return sortable.sort((a, b) => b.endDate.getTime() - a.endDate.getTime());
    }
    
    // Apply sort
    return sortable.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'destination':
          return (getTripPrimaryDestination(a) || '').localeCompare(getTripPrimaryDestination(b) || '');
        case 'date':
        default:
          return a.startDate.getTime() - b.startDate.getTime();
      }
    });
  }, [activeTrips, archivedTrips, upcomingTrips, completedTrips, activeFilter, searchQuery, sortBy]);
  const footerHeavyWidgetsEnabled =
    heavyWidgetGateOpen && (Platform.OS === 'web' || filteredTrips.length <= 2 || footerWidgetsRequested);

  const budgetSummaryTripIds = useMemo(() => {
    const filteredTripIdSet = new Set(filteredTrips.map((trip) => trip.id));
    const seedIds =
      visibleTripIds.length > 0
        ? visibleTripIds.filter((tripId) => filteredTripIdSet.has(tripId))
        : filteredTrips.slice(0, INITIAL_VISIBLE_BUDGET_ROWS).map((trip) => trip.id);

    const tripIds = new Set(seedIds);
    if (featuredTripId) {
      tripIds.add(featuredTripId);
    }
    return Array.from(tripIds);
  }, [featuredTripId, filteredTrips, visibleTripIds]);

  const budgetSummaryTrips = useMemo(
    () =>
      budgetSummaryTripIds
        .map((tripId) => tripById.get(tripId))
        .filter((trip): trip is Trip => Boolean(trip)),
    [budgetSummaryTripIds, tripById],
  );

  // Limit budget listeners on the Trips tab to the featured/visible rows.
  const budgetSnapshots = useTripBudgetSummaries(
    budgetSummaryTrips,
    !!firebaseUser?.uid && !tripsLoading && budgetSummaryTrips.length > 0,
  );

  useEffect(() => {
    if (!passportCountryForVisaCall || !activeTrips.length) {
      setTravelRequirementsByTripId({});
      lastTravelReqsEffectKeyRef.current = null;
      return;
    }

    // Only run API fetches when the logical set of trips or passport country changes.
    // activeTrips is a new array on every Firestore snapshot, which was causing 166+ API calls in a few days.
    const effectKey =
      activeTrips
        .map((t) => t.id)
        .sort()
        .join(',') +
      '|' +
      (passportCountryForVisaCall ?? '');
    if (lastTravelReqsEffectKeyRef.current === effectKey) {
      return;
    }
    lastTravelReqsEffectKeyRef.current = effectKey;

    let cancelled = false;
    const now = Date.now();
    const nextCacheValues: Record<string, TravelRequirements | null> = {};

    activeTrips.forEach((trip) => {
      const destinationLabel = (trip.destination || trip.destinations?.[0]?.city || trip.name || '').toString();
      const fetchKey = `${trip.id}:${destinationLabel}:${passportCountryForVisaCall}`;
      const cached = travelRequirementsCacheRef.current.get(trip.id);

      if (cached && cached.key === fetchKey && now - cached.fetchedAt < TRAVEL_REQUIREMENTS_CACHE_TTL_MS) {
        nextCacheValues[trip.id] = cached.data;
        return;
      }

      if (travelRequirementsInFlightRef.current.has(fetchKey)) return;
      travelRequirementsInFlightRef.current.add(fetchKey);

      getTravelRequirements(trip, passportCountryForVisaCall)
        .then((data) => {
          if (cancelled) return;
          travelRequirementsCacheRef.current.set(trip.id, {
            key: fetchKey,
            data,
            fetchedAt: Date.now(),
          });
          setTravelRequirementsByTripId((prev) => ({ ...prev, [trip.id]: data }));
        })
        .catch(() => {
          if (cancelled) return;
          travelRequirementsCacheRef.current.set(trip.id, {
            key: fetchKey,
            data: null,
            fetchedAt: Date.now(),
          });
          setTravelRequirementsByTripId((prev) => ({ ...prev, [trip.id]: null }));
        })
        .finally(() => {
          travelRequirementsInFlightRef.current.delete(fetchKey);
        });
    });

    if (Object.keys(nextCacheValues).length > 0) {
      setTravelRequirementsByTripId((prev) => ({ ...prev, ...nextCacheValues }));
    }

    return () => {
      cancelled = true;
    };
  }, [activeTrips, passportCountryForVisaCall]);

  const tripAlerts = useMemo(() => {
    if (!firebaseUser?.uid) return [];
    const baseAlerts = generateAllTripAlerts(activeTrips, firebaseUser.uid, {
      citizenship: profile?.citizenship,
      travelRequirementsByTripId,
    });
    const budgetAwareAlerts = generateAllTripAlerts(budgetSummaryTrips, firebaseUser.uid, {
      budgetSnapshots,
      citizenship: profile?.citizenship,
      travelRequirementsByTripId,
    });
    const mergedAlerts = new Map<string, TripAlert>();
    baseAlerts.forEach((alert) => {
      mergedAlerts.set(alert.id, alert);
    });
    budgetAwareAlerts.forEach((alert) => {
      mergedAlerts.set(alert.id, alert);
    });
    return Array.from(mergedAlerts.values());
  }, [
    activeTrips,
    budgetSummaryTrips,
    budgetSnapshots,
    firebaseUser?.uid,
    profile?.citizenship,
    travelRequirementsByTripId,
  ]);

  const handleFilteredTripAlerts = useCallback((next: TripAlert[]) => {
    // Prevent infinite render loops when the notifications sync effect runs
    // but the derived alert list is logically identical.
    setFilteredTripAlerts((prev) => {
      if (prev === next) return prev;
      if (prev.length !== next.length) return next;
      const prevKey = prev.map((a) => a.id).sort().join('|');
      const nextKey = next.map((a) => a.id).sort().join('|');
      return prevKey === nextKey ? prev : next;
    });
  }, []);

  useTripNotificationSync({
    alerts: tripAlerts,
    onFilteredAlerts: handleFilteredTripAlerts,
  });

  const tripNotifications = useNotificationList('trip');
  const alertCount = useNotificationCount('trip');

  // Send push notifications for budget alerts
  useBudgetAlertNotifications(filteredTripAlerts, activeTrips);
  useWeatherAlerts(tripsLoading ? [] : activeTrips);

  const featuredTripAlerts = useMemo(() => {
    const tripId = featuredTripHighlight?.trip.id;
    if (!tripId) return [];
    return filteredTripAlerts.filter((alert) => alert.tripId === tripId);
  }, [featuredTripHighlight?.trip.id, filteredTripAlerts]);

  const featuredTripBriefing = useDayBriefing(featuredTripHighlight?.trip ?? null, {
    budgetSnapshot: featuredTripHighlight?.trip.id ? budgetSnapshots[featuredTripHighlight.trip.id] : undefined,
    alerts: featuredTripAlerts,
    enabled: Boolean(featuredTripHighlight?.trip),
  });
  useDayBriefingNotifications(featuredTripBriefing);

  const lastBriefingViewKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!featuredTripHighlight?.trip.id || !featuredTripBriefing) return;
    const surface = shouldShowBottomSheet
      ? 'bottom_sheet'
      : shouldShowMinimizedHeaderChip
        ? 'minimized_tray'
        : shouldShowHeaderCard
          ? 'active_trip_card'
          : null;
    if (!surface) return;
    const viewKey = `${featuredTripHighlight.trip.id}:${surface}:${featuredTripBriefing.dayContext.todayKey}`;
    if (lastBriefingViewKeyRef.current === viewKey) return;
    lastBriefingViewKeyRef.current = viewKey;
    void logAnalyticsEvent('day_briefing_view', {
      tripId: featuredTripHighlight.trip.id,
      surface,
      itemCount: featuredTripBriefing.items.length,
      hasWeather: Boolean(featuredTripBriefing.weather),
      tripDay: featuredTripBriefing.dayContext.currentDay,
    });
  }, [
    featuredTripBriefing,
    featuredTripHighlight?.trip.id,
    shouldShowBottomSheet,
    shouldShowHeaderCard,
    shouldShowMinimizedHeaderChip,
  ]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    // The subscription will automatically update, just wait a bit for visual feedback
    try {
      // Force a refresh by checking Gmail connection and other services
      if (firebaseUser?.uid) {
        await Promise.all([
          checkGmailConnection().then(setGmailConnected).catch(() => setGmailConnected(false)),
        ]);
      }
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  }, [firebaseUser?.uid]);

  const handleOfflineMaps = useCallback(() => {
    router.push(getMapsTabTarget('merged'));
  }, [router]);

  const displayName = profile.name ?? 'Traveler';
  // A user is truly "new" if they have no trips yet, regardless of onboarding status
  // Only check after trips have finished loading to avoid showing wrong message during load
  const hasTrips = trips.length > 0;
  const isActuallyNewUser = !tripsLoading && !hasTrips;
  const title = 'Trips';
  const greetingTitle = isActuallyNewUser
    ? `Welcome to Tripvio, ${displayName}`
    : `Let’s plan your next adventure, ${displayName}`;
  const greetingSubtitle = isActuallyNewUser
    ? 'Plan your first trip or let Tripvio AI map it out in minutes.'
    : 'From planning to memories, all your trips live here.';

  const handleConnectEmail = useCallback(async () => {
    // Entering provider-selection flow should never resume the quick Gmail-only sync path.
    pendingQuickGmailSyncRef.current = false;
    oauthUserInitiatedRef.current = true;
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to connect your email.');
      return;
    }
    if (!canStartNewTrip) {
      openPremiumGateWithEmailSyncClosed({
        feature: 'trip_limit',
        title: '1 active trip on Free',
        message: 'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
        source: 'feature_gate',
      });
      return;
    }

    try {
      // Always show provider selection first so users can switch providers/accounts.
      // If a provider is already connected, the user can tap it to scan immediately.
      if (connectedInboxProvider) {
        setSelectedEmailProvider(connectedInboxProvider);
      }

      // Show UI immediately so the user gets instant feedback.
      setShowEmailProviderSelection(true);

      await useInboxConnectionsStore.getState().refreshCached();
      const nextConnections = useInboxConnectionsStore.getState().connections;
      const orderProbe: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
      const connectedProvider = orderProbe.find((p) => nextConnections[p]) ?? null;

      if (connectedProvider) {
        // Keep selection aligned to a real connected provider, but stay in the chooser UI.
        setSelectedEmailProvider(connectedProvider);
        setLastUsedInboxProvider(firebaseUser.uid, connectedProvider).catch(() => {});
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[TripsScreen] Error checking email connection', { message });
      // On error, show provider selection as fallback
      setShowEmailProviderSelection(true);
    }
  }, [firebaseUser?.uid, connectedInboxProvider, inboxStatusLoading, canStartNewTrip, openPremiumGateWithEmailSyncClosed]);

  const handleConnectProvider = useCallback(async (provider: InboxProviderId) => {
    pendingQuickGmailSyncRef.current = false;
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to connect your email.');
      return;
    }

    oauthUserInitiatedRef.current = true;

    const cachedProviderConnected = Boolean(useInboxConnectionsStore.getState().connections[provider]);
    if (cachedProviderConnected) {
      pendingQuickGmailSyncRef.current = false;
      useInboxConnectionsStore.getState().setProviderConnected(provider, true);
      setConnectedInboxProvider(provider);
      setSelectedEmailProvider(provider);
      setShowEmailProviderSelection(false);
      showInfo(`Scanning ${PROVIDER_LABELS[provider]} for trips...`);
      presentDetectedTripsSafely(provider);
      oauthUserInitiatedRef.current = false;
      setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
      return;
    }

    // On web, open a placeholder window synchronously only when the cached state says
    // this provider is not already connected. Connected providers should scan directly;
    // opening about:blank first caused the "quick window opens and closes" symptom.
    let popup: Window | null = null;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        popup = window.open('about:blank', '_blank');
      } catch {
        popup = null;
      }
    }

    setConnectingProvider(provider);
    try {
      const status = await checkInboxStatus(provider);
      useInboxConnectionsStore.getState().setProviderConnected(provider, Boolean(status?.connected));
      
      // Handle iCloud/IMAP - would need IMAP modal, but for now just show message
      if (provider === 'icloud' && (!status || !status.connected)) {
        popup?.close?.();
        Alert.alert(
          'iCloud Setup',
          'iCloud requires IMAP settings. Please configure this in Settings > Email Sync.'
        );
        setConnectingProvider(null);
        oauthUserInitiatedRef.current = false;
        return;
      }

      // If requires auth, initiate authentication
      if (status?.requiresAuth || !status?.connected) {
        await setInboxOauthPending(provider).catch(() => {});
        const authResponse = await initiateInboxAuth(provider);
        if (authResponse?.authUrl) {
          if (Platform.OS === 'web') {
            // If popup got blocked, fall back to same-tab navigation.
            if (popup) {
              try {
                popup.location.href = authResponse.authUrl;
              } catch {
                window.location.href = authResponse.authUrl;
              }
            } else {
              // Popup blocked
              window.location.href = authResponse.authUrl;
            }
            showInfo(authResponse.message || 'Continue in browser to finish connecting your inbox.');
            setShowEmailProviderSelection(false);
            setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
          } else {
            const canOpen = await Linking.canOpenURL(authResponse.authUrl);
            if (canOpen) {
              const redirectUrl = Linking.createURL(
                provider === 'gmail' ? 'gmail-callback' : 'outlook-callback',
              );
              const result = await WebBrowser.openAuthSessionAsync(authResponse.authUrl, redirectUrl);
              if (result.type === 'success') {
                showInfo(authResponse.message || 'Continue in browser to finish connecting your inbox.');
                setShowEmailProviderSelection(false);
                setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
              } else {
                // Some auth sessions return "dismiss/cancel" even though provider finished OAuth.
                // Confirm connection status before surfacing a cancellation message.
                const connectedAfterReturn = await checkInboxStatus(provider).catch(() => null);
                if (connectedAfterReturn?.connected) {
                  // Tokens may still be propagating; useInboxOauthResume will call onConnected
                  // while oauthUserInitiatedRef stays true (no clear in finally).
                  showInfo('Connected, starting sync...');
                  setShowEmailProviderSelection(false);
                  setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
                } else {
                  // User cancelled / app backgrounded. Keep provider selection open so they can retry.
                  showInfo('Authentication cancelled. Please try again.');
                  oauthUserInitiatedRef.current = false;
                }
              }
            } else {
              Alert.alert('Connect inbox', 'Unable to open the authorization page automatically.');
              oauthUserInitiatedRef.current = false;
            }
          }
        } else {
          popup?.close?.();
          Alert.alert('Connect inbox', authResponse?.message || 'Unable to start authorization for this provider.');
          oauthUserInitiatedRef.current = false;
        }
      } else if (status?.connected) {
        popup?.close?.();
        // Already connected, proceed to trip detection
        pendingQuickGmailSyncRef.current = false;
        useInboxConnectionsStore.getState().setProviderConnected(provider, true);
        setConnectedInboxProvider(provider);
        setSelectedEmailProvider(provider);
        setShowEmailProviderSelection(false);
        presentDetectedTripsSafely(provider);
        oauthUserInitiatedRef.current = false;
        setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
      }
    } catch (error) {
      popup?.close?.();
      setConnectionProgress(null);
      console.error('[TripsScreen] Error connecting provider:', error);
      const rateLimited = inboxCallableRateLimitUserMessage(error);
      if (rateLimited) {
        Alert.alert('Please wait', rateLimited);
        oauthUserInitiatedRef.current = false;
        return;
      }
      const message =
        (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string'
          ? (error as any).message
          : (error instanceof Error ? error.message : String(error))) || 'Unknown error';

      // Provide actionable setup guidance for common configuration issues.
      if (provider === 'gmail' && /Gmail OAuth credentials are not configured/i.test(message)) {
        Alert.alert(
          'Gmail setup required',
          [
            'Gmail sync isn’t configured for this environment yet.',
            '',
            'For emulator use:',
            '- Copy `functions/env.example` to `functions/.env`',
            '- Set `GMAIL_WEB_CLIENT_ID` and `GMAIL_WEB_CLIENT_SECRET`',
            '',
            'For production:',
            '- Configure Firebase secrets `GMAIL_WEB_CLIENT_ID` and `GMAIL_WEB_CLIENT_SECRET`',
            '',
            'See: `GMAIL_OAUTH_SETUP.md`',
          ].join('\n'),
        );
        oauthUserInitiatedRef.current = false;
        return;
      }

      // Fallback: show the most useful message we have (but keep it user-friendly).
      const friendly =
        /permission|unauth|auth/i.test(message)
          ? 'Authentication error. Please try again.'
          : /network|fetch|timeout/i.test(message)
            ? 'Network error. Please check your connection and try again.'
            : 'Unable to connect email provider. Please try again.';

      Alert.alert('Error', __DEV__ ? `${friendly}\n\n${message}` : friendly);
      oauthUserInitiatedRef.current = false;
    } finally {
      setConnectingProvider(null);
      // Do not clear oauthUserInitiatedRef here: native OAuth returns before Firestore tokens exist.
      // useInboxOauthResume later calls onConnected; clearing in finally made wasUserInitiated false
      // so trip detection never opened. Ref is cleared in handleOauthConnected / cancel paths above.
    }
  }, [firebaseUser?.uid, presentDetectedTripsSafely, showInfo]);

  const handleDisconnectProvider = useCallback(async (provider: InboxProviderId) => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to manage connected emails.');
      return;
    }

    const confirmed = await confirmDisconnectProvider(provider);
    if (!confirmed) return;

    setDisconnectingProvider(provider);
    try {
      const ok = await disconnectEmailProvider(provider);
      if (!ok) {
        throw new Error('Disconnect operation returned unsuccessful response');
      }

      await useInboxConnectionsStore.getState().refreshFresh();
      const conn = useInboxConnectionsStore.getState().connections;
      useInboxConnectionsStore.getState().setProviderConnected(provider, false);
      if (connectedInboxProvider === provider) {
        const fallbackProvider = (['gmail', 'outlook', 'icloud'] as InboxProviderId[]).find(
          (candidate) => candidate !== provider && conn[candidate],
        ) ?? null;
        setConnectedInboxProvider(fallbackProvider);
        if (selectedEmailProvider === provider && fallbackProvider) {
          setSelectedEmailProvider(fallbackProvider);
        }
      }
      showSuccess(`${PROVIDER_LABELS[provider]} disconnected`);
    } catch (error) {
      console.error('[TripsScreen] Error disconnecting provider:', error);
      Alert.alert('Disconnect failed', 'Unable to disconnect this provider right now.');
    } finally {
      setDisconnectingProvider(null);
    }
  }, [firebaseUser?.uid, connectedInboxProvider, selectedEmailProvider, showSuccess]);

  const handleAskTripFlowAi = useCallback(() => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in again to use Tripvio AI.');
      return;
    }
    if (!canStartNewTrip) {
      openPremiumGateWithEmailSyncClosed({
        feature: 'trip_limit',
        title: '1 active trip on Free',
        message: 'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
        source: 'feature_gate',
      });
      return;
    }
    setShowAIAssistant(true);
  }, [firebaseUser?.uid, canStartNewTrip, openPremiumGateWithEmailSyncClosed]);

  const handleNewTripAction = useCallback(() => {
    // Always show the "Start a trip" menu first.
    // Importing/detecting from email should only happen after the user explicitly taps it.
    setShowNewTripOptions(true);
  }, []);

  // Helper to pick a reasonable future start date when none provided (3-6 months out)
  const computeFlexibleStartDate = useCallback((durationInDays?: number): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Set dates 3-6 months in the future (90-180 days)
    const duration = durationInDays && durationInDays > 0 ? durationInDays : 7;
    // Use 4.5 months (135 days) as base, adjust slightly for longer trips
    const baseOffset = duration >= 10 ? 150 : 120; // longer trips -> a bit further out

    const start = new Date(today);
    start.setDate(start.getDate() + baseOffset);

    // Align to the next Monday for nicer itineraries
    const dayOfWeek = start.getDay();
    const daysUntilMonday = (8 - dayOfWeek) % 7;
    start.setDate(start.getDate() + daysUntilMonday);

    return start;
  }, []);

  const handleRebookTrip = useCallback((trip: Trip) => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to rebook this trip.');
      return;
    }
    if (!canStartNewTrip) {
      openPremiumGateWithEmailSyncClosed({
        feature: 'trip_limit',
        title: '1 active trip on Free',
        message:
          'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
        source: 'feature_gate',
      });
      return;
    }

    const millisInDay = 1000 * 60 * 60 * 24;
    const historicalDurationDays = Math.max(
      1,
      Math.round((trip.endDate.getTime() - trip.startDate.getTime()) / millisInDay),
    );
    const startDate = computeFlexibleStartDate(historicalDurationDays);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + historicalDurationDays);
    const destination = getTripPrimaryDestination(trip);

    setManualFormPrefill({
      tripName: destination ? `${destination} Rebook` : `${trip.name} Rebook`,
      travelingFrom: trip.travelingFrom,
      destination,
      startDate,
      endDate,
      travelMode: trip.travelMode,
    });
    setShowManualForm(true);
  }, [firebaseUser?.uid, canStartNewTrip, openPremiumGateWithEmailSyncClosed, computeFlexibleStartDate]);

  const createTripFromRecommendation = useCallback(async (recommendation: TripRecommendation, startDate: Date, endDate: Date): Promise<string | null> => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in again.');
      return null;
    }
    if (!canStartNewTrip) {
      openPremiumGateWithEmailSyncClosed({
        feature: 'trip_limit',
        title: '1 active trip on Free',
        message: 'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
        source: 'feature_gate',
      });
      return null;
    }

    try {
      const tripData: CreateTripData = {
        name: `${recommendation.destination} Trip`,
        destination: recommendation.destination,
        startDate,
        endDate,
        travelMode: 'mixed',
      };

      const recommendationOrigin =
        typeof (recommendation as Record<string, unknown>).travelingFrom === 'string'
          ? ((recommendation as Record<string, unknown>).travelingFrom as string).trim()
          : undefined;

      const inferredOrigin = recommendationOrigin || profile.homeCountry || profile.citizenship || undefined;
      if (inferredOrigin) {
        tripData.travelingFrom = inferredOrigin;
      }

      const tripId = await createTrip(firebaseUser.uid, tripData);

      const hasHighlights =
        Array.isArray(recommendation.itineraryHighlights) &&
        recommendation.itineraryHighlights.some((line) => line?.trim());

      let itineraryCreatedCount = 0;
      if (hasHighlights) {
        const itineraryResult = await createItineraryFromRecommendationHighlights({
          tripId,
          userId: firebaseUser.uid,
          recommendation,
          tripStartDate: startDate,
        });
        itineraryCreatedCount = itineraryResult.createdCount;
      }

      const itineraryNote =
        itineraryCreatedCount > 0
          ? ` We added ${itineraryCreatedCount} draft plan item${itineraryCreatedCount === 1 ? '' : 's'} to your itinerary.`
          : '';

      // Show success feedback first, then the optional date-edit prompt.
      showSuccess(`${recommendation.destination} trip created`);
      setTimeout(() => {
        Alert.alert(
          'Trip Created!',
          `Your ${recommendation.destination} trip has been created.${itineraryNote} Want to adjust the dates now?`,
          [
            {
              text: 'Keep Suggested Dates',
              style: 'cancel',
            },
            {
              text: 'Edit Dates Now',
              onPress: () => {
                // Find the trip in the trips array by ID
                const createdTrip = trips.find(t => t.id === tripId);
                if (createdTrip) {
                  setSelectedTripForEdit(createdTrip);
                } else {
                  // If trip not found yet, wait a bit and try again
                  setTimeout(() => {
                    const trip = trips.find(t => t.id === tripId);
                    if (trip) {
                      setSelectedTripForEdit(trip);
                    } else {
                      showInfo('Trip created! You can edit it from the trip details.');
                    }
                  }, 500);
                }
              },
            },
          ]
        );
      }, 350);
      
      return tripId;
    } catch (error) {
      console.error('Failed to create trip from recommendation:', error);
      showError('Failed to create trip. Please try again.');
      return null;
    }
  }, [firebaseUser?.uid, profile, showError, showInfo, showSuccess, trips, canStartNewTrip, openPremiumGateWithEmailSyncClosed]);

  const handleSelectRecommendation = useCallback(async (recommendation: TripRecommendation) => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in again.');
      return;
    }

    // Always create trip immediately with computed best dates
    const duration = recommendation.duration && recommendation.duration > 0 ? recommendation.duration : 7;
    const startDate = computeFlexibleStartDate(duration);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + duration);

    await createTripFromRecommendation(recommendation, startDate, endDate);
  }, [firebaseUser?.uid, computeFlexibleStartDate, createTripFromRecommendation]);


  // Use shared notification action handler for consistent behavior
  const {
    handleAction: baseHandleNotificationAction,
    handleDismiss: handleNotificationDismiss,
    handleDismissAll: handleNotificationDismissAll,
  } = useNotificationActionHandler({
    context: 'trips',
    callbacks: {
      onClose: () => setShowNotifications(false),
      onOpenCollaboration: (tripId) => {
        const trip = trips.find((t) => t.id === tripId);
        if (trip) {
          setSelectedTripForCollaboration(trip);
          return;
        }
        if (!tripId || !firebaseUser?.uid) {
          return;
        }
        void getTripById(tripId, { fromServer: true }).then((fetchedTrip) => {
          if (!fetchedTrip) {
            return;
          }
          notifyLiveTripUpdated(firebaseUser.uid, fetchedTrip);
          setTrips((prev) => {
            if (prev.some((existing) => existing.id === fetchedTrip.id)) {
              return prev.map((existing) => (existing.id === fetchedTrip.id ? fetchedTrip : existing));
            }
            return [...prev, fetchedTrip];
          });
          setSelectedTripForCollaboration(fetchedTrip);
        });
      },
      onActionTracked: async (notification, action) => {
        const matchingAlert = filteredTripAlerts.find((alert) => alert.id === notification.id);
        const tripId = matchingAlert?.tripId ?? notification.scopeId;
        if (tripId && matchingAlert) {
          try {
            const { trackAlertManaged } = await import('@/services/tripAlertTracking');
            await trackAlertManaged(tripId, matchingAlert, action);
          } catch (error) {
            console.error('[TripsScreen] Error tracking alert:', error);
          }
        }
      },
    },
  });

  // Wrap the base handler to add tracking
  const handleNotificationAction = useCallback(
    (notification: Notification) => {
      baseHandleNotificationAction(notification);
    },
    [baseHandleNotificationAction],
  );

  // Wrapper to handle notification dismiss by ID
  const handleClearAlert = useCallback(
    async (notificationId: string) => {
      const notif = tripNotifications.find((n) => n.id === notificationId);
      if (notif) {
        await handleNotificationDismiss(notif);
      }
      setFilteredTripAlerts((prev) => prev.filter((alert) => alert.id !== notificationId));
    },
    [tripNotifications, handleNotificationDismiss],
  );

  // Wrapper for dismiss all
  const handleClearAll = useCallback(async () => {
    if (tripNotifications.length === 0) return;
    await handleNotificationDismissAll(tripNotifications);
    setFilteredTripAlerts([]);
  }, [tripNotifications, handleNotificationDismissAll]);

  // TEMPORARY: Cleanup duplicates function
  const handleCleanupDuplicates = useCallback(async () => {
    if (cleanupInProgress) return;

    // Ask user if they want to delete ALL templates or just duplicates
    Alert.alert(
      'Delete Templates',
      'Delete ALL templates or just duplicates?',
      [
        {
          text: 'Cancel',
          style: 'cancel'
        },
        {
          text: 'Just Duplicates',
          onPress: async () => {
            setCleanupInProgress(true);
            showInfo('Cleaning up duplicates...');
            try {
              const result = await cleanupDuplicateTemplates(false);
              if (result.success) {
                showSuccess(`Deleted ${result.deleted} duplicates, kept ${result.kept} templates.`);
              } else {
                showError(`Cleanup failed: ${result.error}`);
              }
            } catch (error) {
              showError('Error cleaning up duplicates');
            } finally {
              setCleanupInProgress(false);
            }
          }
        },
        {
          text: 'Delete ALL',
          style: 'destructive',
          onPress: async () => {
            setCleanupInProgress(true);
            showInfo('Deleting all templates...');
            try {
              const result = await cleanupDuplicateTemplates(true);
              if (result.success) {
                showSuccess(`Deleted all ${result.deleted} templates.`);
              } else {
                showError(`Cleanup failed: ${result.error}`);
              }
            } catch (error) {
              showError('Error deleting templates');
            } finally {
              setCleanupInProgress(false);
            }
          }
        }
      ]
    );
  }, [cleanupInProgress, showInfo, showSuccess, showError]);

  // Template handlers
  const handleBrowseTemplates = useCallback(() => {
    if (__DEV__) {
      console.log('[TripsScreen] Opening template browser');
    }
    setTemplateBrowserInitialTopic(undefined);
    setActiveTemplateModal('browser');
  }, [setActiveTemplateModal]);

  const handleTemplateBrowserInitialTopicConsumed = useCallback(() => {
    setTemplateBrowserInitialTopic(undefined);
  }, []);

  const handleTemplateDeeplinkAttributionConsumed = useCallback(() => {
    setTemplateDeeplinkRef(undefined);
    setTemplateDeeplinkUtmSource(undefined);
  }, []);

  type TemplateEntryPoint = 'browser' | 'widget' | 'deeplink' | null;
  const [templateEntryPoint, setTemplateEntryPoint] = useState<TemplateEntryPoint>(null);
  type TemplateModalTransition =
    | 'openDetail'
    | 'reopenBrowser'
    | 'navigateToPlan'
    | 'showTripLimitGate'
    | null;
  const pendingTemplateTransitionRef = useRef<TemplateModalTransition>(null);
  const pendingTemplateRouteTripIdRef = useRef<string | null>(null);
  const templateCustomizationStartedAtRef = useRef<number | null>(null);
  const templateCustomizationShouldClearOnDismissRef = useRef(false);
  /** Prior modal visibility — used because RN `Modal.onDismiss` often does not fire on Android API < 31. */
  const prevTemplateBrowserVisibleRef = useRef(showTemplateBrowser);
  const prevTemplateDetailVisibleRef = useRef(showTemplateDetail);
  const prevTemplateCustomizationVisibleRef = useRef(showTemplateCustomization);

  useEffect(() => {
    const openTemplatesRaw = routeParams.openTemplates;
    const openTemplates =
      openTemplatesRaw === '1' ||
      openTemplatesRaw === 'true' ||
      (Array.isArray(openTemplatesRaw) &&
        (openTemplatesRaw[0] === '1' || openTemplatesRaw[0] === 'true'));
    const topic = normalizeTemplateTopicParam(routeParams.templateTopic);
    const templateId = normalizeTemplateIdParam(routeParams.templateId);
    const hasDeepLinkParams = Boolean(openTemplates || topic || templateId);

    if (!hasDeepLinkParams) {
      templateDeepLinkHandledRef.current = false;
      return;
    }

    if (templateDeepLinkHandledRef.current) {
      return;
    }

    templateDeepLinkHandledRef.current = true;

    const authState = (() => {
      const state = useUserStore.getState();
      if (!state.isAuthenticated) return 'signed_out' as const;
      return state.hasCompletedOnboarding ? 'signed_in_onboarded' as const : 'signed_in_not_onboarded' as const;
    })();
    const refParam = Array.isArray(routeParams.ref) ? routeParams.ref[0] : routeParams.ref;
    const utmSourceParam = Array.isArray(routeParams.utm_source) ? routeParams.utm_source[0] : routeParams.utm_source;
    const deeplinkRef = typeof refParam === 'string' ? refParam : undefined;
    const deeplinkUtmSource = typeof utmSourceParam === 'string' ? utmSourceParam : undefined;

    setTemplateDeeplinkRef(deeplinkRef);
    setTemplateDeeplinkUtmSource(deeplinkUtmSource);

    logTemplateDeepLinkAnalytics({
      topic,
      templateId,
      ref: deeplinkRef,
      utmSource: deeplinkUtmSource,
      authState,
    });

    router.setParams({
      openTemplates: undefined,
      templateTopic: undefined,
      templateId: undefined,
      ref: undefined,
      utm_source: undefined,
    } as never);

    if (templateId) {
      void getTemplateById(templateId)
        .then((template) => {
          if (template) {
            setSelectedTemplate(template);
            setSelectedTemplateTopicTag(topic);
            setTemplateEntryPoint('deeplink');
            setActiveTemplateModal('detail');
            return;
          }
          setTemplateBrowserInitialTopic(topic);
          setActiveTemplateModal('browser');
        })
        .catch((error) => {
          console.error('[TripsScreen] Failed to open template deeplink:', error);
          setTemplateBrowserInitialTopic(topic);
          setActiveTemplateModal('browser');
        });
      return;
    }

    setTemplateBrowserInitialTopic(topic);
    setActiveTemplateModal('browser');
  }, [
    routeParams.openTemplates,
    routeParams.templateId,
    routeParams.templateTopic,
    routeParams.ref,
    routeParams.utm_source,
    router,
    setActiveTemplateModal,
  ]);

  const handleSelectTemplate = useCallback((template: TripTemplate, source: TemplateEntryPoint = 'widget', topicTag?: string) => {
    if (__DEV__) {
      console.log('[TripsScreen] Template selected', { id: template.id, source });
    }
    if (source === 'widget') {
      void logAnalyticsEvent('template_viewed', {
        templateId: template.id,
        templateTitle: template.title,
        destination: template.destination,
        duration: template.duration,
        source: 'quick_widget',
      });
    }
    setSelectedTemplate(template);
    setSelectedTemplateTopicTag(topicTag);
    setTemplateEntryPoint(source);
    if (source === 'browser') {
      // Queue detail for after browser fully dismisses to avoid iOS modal presentation collisions.
      pendingTemplateTransitionRef.current = 'openDetail';
      setActiveTemplateModal(null);
      return;
    }
    pendingTemplateTransitionRef.current = null;
    setActiveTemplateModal('detail');
  }, []);

  const handleUseTemplate = useCallback(
    (template: TripTemplate) => {
      if (__DEV__) {
        console.log('[TripsScreen] handleUseTemplate: switching detail modal to customization', {
          templateId: template.id,
          templateEntryPoint,
        });
      }

      if (!tripsLoading && !canStartNewTripLive) {
        templateCustomizationShouldClearOnDismissRef.current = true;
        pendingTemplateTransitionRef.current = 'showTripLimitGate';
        setActiveTemplateModal(null);
        return;
      }

      setSelectedTemplate(template);
      templateCustomizationStartedAtRef.current = Date.now();
      const templateSource =
        templateEntryPoint === 'widget'
          ? 'quick_widget'
          : templateEntryPoint === 'deeplink'
            ? 'deeplink'
            : templateEntryPoint === 'browser'
              ? 'browser'
              : 'unknown';
      void logAnalyticsEvent('template_customization_started', {
        templateId: template.id,
        templateTitle: template.title,
        source: templateSource,
        topicTag: selectedTemplateTopicTag,
      });

      pendingTemplateTransitionRef.current = null;
      setActiveTemplateModal('customization');
    },
    [
      templateEntryPoint,
      selectedTemplateTopicTag,
      setActiveTemplateModal,
      tripsLoading,
      canStartNewTripLive,
    ],
  );

  const presentTripLimitGate = useCallback(() => {
    openPremiumGateWithEmailSyncClosed({
      feature: 'trip_limit',
      title: '1 active trip on Free',
      message:
        'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
      source: 'feature_gate',
    });
  }, [openPremiumGateWithEmailSyncClosed]);

  // After chaining modals (browser → detail), queued steps rely on Modal `onDismiss`.
  // Android often never calls `onDismiss` for programmatic `visible={false}`, so transitions never run.
  useEffect(() => {
    const wasBrowser = prevTemplateBrowserVisibleRef.current;
    const wasDetail = prevTemplateDetailVisibleRef.current;
    const wasCustomization = prevTemplateCustomizationVisibleRef.current;
    const pending = pendingTemplateTransitionRef.current;
    const canUseVisibilityFallback = Platform.OS !== 'ios';

    if (
      canUseVisibilityFallback &&
      pending === 'openDetail' &&
      wasBrowser &&
      !showTemplateBrowser &&
      selectedTemplate
    ) {
      pendingTemplateTransitionRef.current = null;
      requestAnimationFrame(() => setActiveTemplateModal('detail'));
    } else if (
      canUseVisibilityFallback &&
      pending === 'reopenBrowser' &&
      wasDetail &&
      !showTemplateDetail &&
      !showTemplateCustomization
    ) {
      pendingTemplateTransitionRef.current = null;
      requestAnimationFrame(() => setActiveTemplateModal('browser'));
    } else if (
      canUseVisibilityFallback &&
      pending === 'reopenBrowser' &&
      wasCustomization &&
      !showTemplateCustomization &&
      !showTemplateDetail
    ) {
      pendingTemplateTransitionRef.current = null;
      requestAnimationFrame(() => setActiveTemplateModal('browser'));
      setSelectedTemplate(null);
      setSelectedTemplateTopicTag(undefined);
      setTemplateEntryPoint(null);
      templateCustomizationShouldClearOnDismissRef.current = false;
    } else if (
      canUseVisibilityFallback &&
      pending === 'navigateToPlan' &&
      wasCustomization &&
      !showTemplateCustomization &&
      !showTemplateDetail
    ) {
      const tripId = pendingTemplateRouteTripIdRef.current;
      pendingTemplateTransitionRef.current = null;
      pendingTemplateRouteTripIdRef.current = null;
      setSelectedTemplate(null);
      setSelectedTemplateTopicTag(undefined);
      setTemplateEntryPoint(null);
      templateCustomizationShouldClearOnDismissRef.current = false;
      if (tripId) {
        requestAnimationFrame(() => {
          router.push({
            pathname: '/(tabs)/plan',
            params: { tripId },
          });
        });
      }
    } else if (
      canUseVisibilityFallback &&
      pending === 'showTripLimitGate' &&
      (wasCustomization || wasDetail) &&
      !showTemplateCustomization &&
      !showTemplateDetail
    ) {
      pendingTemplateTransitionRef.current = null;
      setSelectedTemplate(null);
      setSelectedTemplateTopicTag(undefined);
      setTemplateEntryPoint(null);
      templateCustomizationShouldClearOnDismissRef.current = false;
      requestAnimationFrame(() => {
        presentTripLimitGate();
      });
    }

    prevTemplateBrowserVisibleRef.current = showTemplateBrowser;
    prevTemplateDetailVisibleRef.current = showTemplateDetail;
    prevTemplateCustomizationVisibleRef.current = showTemplateCustomization;
  }, [
    showTemplateBrowser,
    showTemplateDetail,
    showTemplateCustomization,
    selectedTemplate,
    setActiveTemplateModal,
    presentTripLimitGate,
    router,
  ]);

  const handleConfirmTemplateCustomization = useCallback(async (customization: any) => {

    const isActiveTripLimitError = (error: unknown): boolean => {
      const e = error as { code?: string; message?: string };
      const code = `${e?.code ?? ''}`;
      const msg = `${e?.message ?? ''}`.toLowerCase();
      if (/failed-precondition/i.test(code) && (/\bactive trip\b|\bfree plan\b|premium/i.test(msg) || /upgrade/i.test(msg))) {
        return true;
      }
      if (/\b1 active trip\b|\bfree plan allows\b|upgrade to premium/i.test(msg)) {
        return true;
      }
      return false;
    };

    if (creatingTemplate) {
      throw new PremiumGatePresentedError(TEMPLATE_CREATE_IN_PROGRESS_REASON);
    }
    if (!firebaseUser?.uid || !selectedTemplate) {
      showError('Please sign in to create a trip');
      throw new PremiumGatePresentedError('sign_in_required');
    }
    // Guard with live data only; avoid false-positive paywall from transient cache/loading state.
    const shouldGateLocallyForTripLimit = !tripsLoading && !canStartNewTripLive;
    if (shouldGateLocallyForTripLimit) {
      throw new PremiumGatePresentedError('trip_limit');
    }

    try {
      setCreatingTemplate(true);
      const templateSource =
        templateEntryPoint === 'widget'
          ? 'quick_widget'
          : templateEntryPoint === 'deeplink'
            ? 'deeplink'
            : templateEntryPoint === 'browser'
              ? 'browser'
              : 'unknown';
      const tripId = await createTripFromTemplate(firebaseUser.uid, selectedTemplate.id, customization, {
        source: templateSource,
        topicTag: selectedTemplateTopicTag,
      });
      setPendingTemplateTripId(tripId);
      const customizationStartedAt = templateCustomizationStartedAtRef.current;
      void logAnalyticsEvent('template_customization_completed', {
        templateId: selectedTemplate.id,
        tripId,
        customizationTimeMs: customizationStartedAt ? Date.now() - customizationStartedAt : 0,
        source: templateSource,
        topicTag: selectedTemplateTopicTag,
      });
    } catch (error) {
      if (isPremiumGatePresentedError(error)) {
        throw error;
      }
      console.error('[TripsScreen] Error creating trip from template:', error);
      if (isActiveTripLimitError(error)) {
        if (activeOwnedTripCountLive > 0 || (!tripsLoading && !canStartNewTripLive)) {
          throw new PremiumGatePresentedError('trip_limit_server');
        }
        throw new Error(
          'Your account already has an active trip, but this device is showing stale trip data. Pull to refresh on the Trips tab, then try again.'
        );
      }
      const message =
        error instanceof Error ? error.message : 'Failed to create trip from template. Please try again.';
      throw new Error(message);
    } finally {
      setCreatingTemplate(false);
    }
  }, [
    creatingTemplate,
    firebaseUser?.uid,
    selectedTemplate,
    selectedTemplateTopicTag,
    templateEntryPoint,
    showError,
    tripsLoading,
    canStartNewTripLive,
    activeOwnedTripCountLive,
  ]);

  const handleCloseTemplateCustomization = useCallback((reason?: 'user' | 'success' | 'premium_gate') => {
    const createdTripId = pendingTemplateTripId;
    const shouldOpenBrowser =
      reason !== 'premium_gate' && !createdTripId && templateEntryPoint === 'browser';

    templateCustomizationShouldClearOnDismissRef.current = true;
    setActiveTemplateModal(null);

    if (createdTripId && reason === 'success' && selectedTemplate) {
      setPendingTemplateTripId(null);
      pendingTemplateRouteTripIdRef.current = null;
      pendingTemplateTransitionRef.current = null;
      const templateForPrompt = selectedTemplate;
      const topicTagForPrompt = selectedTemplateTopicTag;
      const openCreatedTrip = () => {
        void maybePromptNotificationValue({
          moment: 'template_created',
          source: 'template_apply_success',
        });
        setSelectedTemplate(null);
        setSelectedTemplateTopicTag(undefined);
        setTemplateEntryPoint(null);
        templateCustomizationShouldClearOnDismissRef.current = false;
        InteractionManager.runAfterInteractions(() => {
          router.push({
            pathname: '/(tabs)/plan',
            params: { tripId: createdTripId },
          });
        });
      };
      Alert.alert(
        'Trip created from template',
        'Your itinerary is ready. You can customize it now or share the template with someone else planning a similar trip.',
        [
          { text: 'Customize itinerary', onPress: openCreatedTrip },
          {
            text: 'Share template',
            onPress: () => {
              void shareTemplate(templateForPrompt, {
                source: 'post_use_prompt',
                topicTag: topicTagForPrompt,
                onCopied: () => {
                  showSuccess('Template link copied');
                  openCreatedTrip();
                },
                onShared: () => {
                  openCreatedTrip();
                },
              });
            },
          },
          { text: 'Later', style: 'cancel', onPress: openCreatedTrip },
        ],
      );
    } else if (createdTripId) {
      setPendingTemplateTripId(null);
      pendingTemplateRouteTripIdRef.current = createdTripId;
      pendingTemplateTransitionRef.current = 'navigateToPlan';
    } else if (reason === 'premium_gate') {
      pendingTemplateTransitionRef.current = 'showTripLimitGate';
    } else if (shouldOpenBrowser) {
      pendingTemplateTransitionRef.current = 'reopenBrowser';
    } else {
      pendingTemplateTransitionRef.current = null;
    }
  }, [
    pendingTemplateTripId,
    router,
    selectedTemplate,
    selectedTemplateTopicTag,
    templateEntryPoint,
    setActiveTemplateModal,
  ]);

  // Prepare list data for FlatList
  type ListItem = 
    | { type: 'empty'; id: string }
    | { type: 'search'; id: string }
    | { type: 'section-header'; id: string; title: string; count?: number }
    | { type: 'trip'; id: string; trip: Trip }
    | { type: 'widget'; id: string; widget: 'document' | 'favorites' | 'recommendations' | 'quick-actions' | 'archived' | 'templates' }
    | { type: 'widget-header'; id: string }
    | { type: 'dev-tools'; id: string }; // TEMPORARY: Development tools - REMOVE BEFORE DEPLOYMENT

  const listData = useMemo<ListItem[]>(() => {
    const items: ListItem[] = [];

    if (isActuallyNewUser && !tripsLoading) {
      items.push({ type: 'empty', id: 'empty-state' });
      // Keep templates discoverable even before the first trip exists
      items.push({ type: 'widget', id: 'templates-empty', widget: 'templates' });
    } else if (!isActuallyNewUser) {
      // Search bar
      if (tripsLoading || trips.length > 0) {
        items.push({ type: 'search', id: 'search' });
      }

      // Trips list
      if (tripsLoading) {
        // Skeleton loaders will be handled in renderItem
      } else if (filteredTrips.length > 0) {
        items.push({
          type: 'section-header',
          id: 'trips-header',
          title: activeFilter === 'archived'
            ? 'Archived Trips'
            : activeFilter === 'upcoming'
              ? 'Upcoming Trips'
              : activeFilter === 'completed'
                ? 'Completed Trips'
            : 'Upcoming Trips',
          count: filteredTrips.length,
        });
        filteredTrips.forEach(trip => {
          items.push({ type: 'trip', id: trip.id, trip });
        });
      } else {
        items.push({ type: 'empty', id: 'no-trips' });
      }
    }

    // Ensure templates widget appears exactly once
    const hasTemplateWidget = items.some((item) => item.type === 'widget' && item.widget === 'templates');
    if (firebaseUser?.uid && !hasTemplateWidget) {
      items.push({ type: 'widget', id: 'templates', widget: 'templates' });
    }

    const firstWidgetIndex = items.findIndex((item) => item.type === 'widget');
    if (firstWidgetIndex >= 0) {
      items.splice(firstWidgetIndex, 0, { type: 'widget-header', id: 'widgets-header' });
    }

    return items;
  }, [isActuallyNewUser, tripsLoading, hasActiveTrips, hasArchivedTrips, activeFilter, filteredTrips, firebaseUser?.uid]);

  // Render list item
  const renderItem: ListRenderItem<ListItem> = useCallback(({ item, index }) => {
    switch (item.type) {
      case 'empty':
        if (item.id === 'empty-state') {
          return (
            <>
              <Card>
                <EmptyTripsState
                  onCreateTrip={() => {
                    if (!canStartNewTrip) {
                      openPremiumGateWithEmailSyncClosed({
                        feature: 'trip_limit',
                        title: '1 active trip on Free',
                        message:
                          'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
                        source: 'feature_gate',
                      });
                      return;
                    }
                    setShowManualForm(true);
                    setManualFormPrefill(null);
                  }}
                  onAskAI={handleAskTripFlowAi}
                  onConnectEmail={handleConnectEmail}
                  onBrowseTemplates={handleBrowseTemplates}
                  onSmartUpload={() => {
                    setSmartUploadVisible(true);
                  }}
                  connectEmailTutorialRef={emailSyncButtonRef}
                  askAiTutorialRef={aiAssistantButtonRef}
                />
              </Card>
            </>
          );
        }
        const getEmptyStateMessage = () => {
          if (searchQuery) {
            return 'No trips match your search.';
          }
          if (activeFilter === 'completed') {
            return 'Complete your first trip';
          }
          if (activeFilter === 'archived') {
            return 'Your archived trips live here';
          }
          if (hasArchivedTrips) {
            return 'All of your trips are archived. Unarchive a trip or start a new plan.';
          }
          return 'No trips yet. Create your first trip to get started!';
        };

        return (
          <Card>
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingVertical: theme.spacing(3) }}>
              {getEmptyStateMessage()}
            </Body>
          </Card>
        );

      case 'search':
        return (
          <Card
            variant="elevated"
            elevation={0}
            padding={theme.spacing(1.5)}
            style={{
              backgroundColor: theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
            }}
          >
            <CombinedSearchFilterBar
              searchValue={searchQuery}
              onChangeSearch={setSearchQuery}
              selectedFilter={activeFilter}
              onFilterChange={setActiveFilter}
              upcomingCount={suppressTripFilterCounts ? undefined : upcomingTrips.length}
              completedCount={suppressTripFilterCounts ? undefined : completedTrips.length}
              archivedCount={suppressTripFilterCounts ? undefined : archivedTrips.length}
            />
          </Card>
        );

      case 'section-header':
        return (
          <View>
            <TripListSectionHeader
              title={item.title}
              count={item.count}
              icon={activeFilter === 'archived' ? 'archive-outline' : 'airplane-outline'}
            />
            {activeFilter === 'archived' && (
              <View style={{ paddingHorizontal: theme.spacing(2), paddingTop: theme.spacing(0.5), paddingBottom: theme.spacing(1) }}>
                <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
                  Archived trips are read-only. You can unarchive them to make them active again.
                </Body>
              </View>
            )}
          </View>
        );

      case 'widget-header':
        return null;

      case 'trip':
        return (
          <TripErrorBoundary name="TripCardRow" resetKeys={[item.trip.id]}>
            <TripCardEnhanced
              trip={item.trip}
              isLast={index === listData.length - 1}
              onSharePress={setSelectedTripForCollaboration}
              onActionsPress={setSelectedTripForActions}
              onRebookPress={handleRebookTrip}
              collaboratorCount={item.trip.collaborators?.length}
              nextItem={itineraryHighlights[item.trip.id]}
              budgetSnapshot={budgetSnapshots[item.trip.id]}
              entryPoint="default"
              onOpenItinerary={(tripId, itemId) =>
                router.push({ pathname: '/(tabs)/plan', params: { tripId, itemId } })
              }
              // Avoid mount-time animations for all rows; they can cause scroll jank when virtualization mounts/unmounts many items.
              enableEntranceAnimation={index < 3}
            />
          </TripErrorBoundary>
        );

      case 'widget':
        switch (item.widget) {
          case 'templates':
            return firebaseUser?.uid ? (
              <View ref={templatesWidgetRef} collapsable={false}>
                {heavyWidgetGateOpen ? (
                  <TemplatesQuickWidget
                    onSelectTemplate={handleSelectTemplate}
                    onBrowseAll={handleBrowseTemplates}
                  />
                ) : (
                  <DeferredWidgetPlaceholder
                    icon="map-outline"
                    title="Start with a Template"
                    description="Browse curated itineraries to jumpstart your planning."
                    showSpinner={false}
                    actionLabel="Browse Templates"
                    onAction={handleBrowseTemplates}
                  />
                )}
              </View>
            ) : null;
          case 'document':
            return firebaseUser?.uid ? (
              footerHeavyWidgetsEnabled ? (
                <DocumentExpirationWidget />
              ) : (
                <DeferredWidgetPlaceholder
                  icon="document-text-outline"
                  title="Document Expiration"
                  description="Passport and visa reminders stay deferred until you scroll into this section."
                />
              )
            ) : null;
          case 'recommendations':
            if (!firebaseUser?.uid) return null;
            return (
              <Card
                variant="outlined"
                style={{
                  backgroundColor: theme.colors.backgroundSecondary,
                  borderColor: theme.colors.borderMuted,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1) }}>
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: theme.radii.pill,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.colors.primary + '12',
                    }}
                  >
                    <Ionicons name="list-outline" size={18} color={theme.colors.primary} />
                  </View>
                  <SubHeading>{BUCKET_LIST_TITLE}</SubHeading>
                </View>
                <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
                  Quick access to destinations and ideas you’ve saved for later.
                </Body>
                {footerHeavyWidgetsEnabled ? (
                  <SavedRecommendationsList
                    userId={firebaseUser.uid}
                    maxItems={3}
                    canCreateNewActiveTrip={canStartNewTrip}
                    showCreateTripButton={true}
                    showShareButton={true}
                    showRemoveButton={true}
                    onSelectRecommendation={handleBucketListItemPress}
                    onCreateTripRecommendation={createTripFromRecommendation}
                    onEmptyAskAi={handleAskTripFlowAi}
                    onEmptyBrowseTemplates={handleBrowseTemplates}
                    layout="horizontal"
                    copyVariant="places"
                  />
                ) : (
                  <DeferredInlineWidgetPlaceholder message="Saved destinations stay deferred until you reach this part of the Trips screen." />
                )}
              </Card>
            );
          case 'quick-actions':
            return (
              <QuickActionsWidget
                actions={[
                  {
                    id: 'email-import',
                    label: 'Import bookings from email',
                    icon: 'mail-outline',
                    onPress: handleConnectEmail,
                    status: inboxStatusLoading ? 'syncing' : connectedInboxProvider ? 'connected' : 'disconnected',
                    actionLabel: inboxStatusLoading
                      ? 'Checking...'
                      : connectedInboxProvider
                        ? `Scan (${PROVIDER_LABELS[connectedInboxProvider]})`
                        : 'Connect',
                  },
                  {
                    id: 'smart-upload',
                    label: 'Smart upload travel docs',
                    icon: 'cloud-upload-outline',
                    onPress: () => setSmartUploadVisible(true),
                  },
                  {
                    id: 'docs',
                    label: 'Access your travel docs',
                    icon: 'document-text-outline',
                    onPress: () => router.push(getDocumentsTabTarget('merged')),
                  },
                  {
                    id: 'maps',
                    label: 'Download offline maps',
                    icon: 'map-outline',
                    onPress: handleOfflineMaps,
                  },
                ]}
                onCreateTrip={
                  !isActuallyNewUser
                    ? () => {
                        if (!canStartNewTrip) {
                          openPremiumGateWithEmailSyncClosed({
                            feature: 'trip_limit',
                            title: '1 active trip on Free',
                            message:
                              'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
                            source: 'feature_gate',
                          });
                          return;
                        }
                        setShowManualForm(true);
                        setManualFormPrefill(null);
                      }
                    : undefined
                }
                onAskAI={!isActuallyNewUser ? handleAskTripFlowAi : undefined}
                // Avoid duplicating template entry points for brand-new users (they see the template rail above).
                onBrowseTemplates={!isActuallyNewUser ? handleBrowseTemplates : undefined}
                hasActiveTrips={hasActiveTrips}
              />
            );
          default:
            return null;
        }

      case 'dev-tools':
        // TEMPORARY: Development-only template upload button - REMOVE BEFORE DEPLOYMENT
        if (!__DEV__ || !firebaseUser?.uid) return null;
        return (
          <Card variant="outlined" style={{ backgroundColor: theme.colors.warningContainer + '20', borderColor: theme.colors.warning + '40' }}>
            <View style={{ gap: theme.spacing(1.5) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="construct-outline" size={16} color={theme.colors.warning} />
                <Caption color={theme.colors.warning} style={{ fontWeight: '600' }}>
                  Development Tools
                </Caption>
              </View>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                <Button
                  label="Cleanup"
                  onPress={handleCleanupDuplicates}
                  disabled={cleanupInProgress}
                  variant="ghost"
                  style={{ flex: 1 }}
                  icon={<Ionicons name="trash-outline" size={18} color={theme.colors.error} />}
                />
              </View>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                ⚠️ Remove this section before production deployment
              </Caption>
            </View>
          </Card>
        );

      default:
        return null;
    }
  }, [
    theme,
    searchQuery,
    activeFilter,
    upcomingTrips.length,
    completedTrips.length,
    archivedTrips.length,
    listData.length,
    hasArchivedTrips,
    firebaseUser?.uid,
    isActuallyNewUser,
    heavyWidgetGateOpen,
    footerHeavyWidgetsEnabled,
    canStartNewTrip,
    openPremiumGateWithEmailSyncClosed,
    inboxStatusLoading,
    connectedInboxProvider,
    hasActiveTrips,
    handleConnectEmail,
    handleAskTripFlowAi,
    handleBrowseTemplates,
    handleBucketListItemPress,
    handleOfflineMaps,
    handleSelectTemplate,
    createTripFromRecommendation,
    router,
    setSelectedTripForCollaboration,
    setSelectedTripForActions,
    setShowManualForm,
    handleRebookTrip,
    itineraryHighlights,
    budgetSnapshots,
    cleanupInProgress,
    handleCleanupDuplicates,
  ]);

  const renderListHeader = useCallback(() => {
    const blocks: React.ReactElement[] = [];

    blocks.push(
      <View
        key="greeting"
        style={{
          gap: theme.spacing(0.5),
          paddingHorizontal: theme.spacing(0.5),
          paddingTop: theme.spacing(0.5),
          paddingBottom: theme.spacing(1),
        }}
      >
        <SubHeading style={{ letterSpacing: -0.2 }}>{greetingTitle}</SubHeading>
        <Caption color={theme.colors.textSecondary}>{greetingSubtitle}</Caption>
      </View>
    );

    if (emailSyncResults && emailSyncResults.bookings.length > 0) {
      const tripName = tripById.get(emailSyncResults.tripId)?.name || 'selected trip';
      const providerLabel = PROVIDER_LABELS[emailSyncResults.provider] || 'Email';
      const previewBookings = emailSyncResults.bookings.slice(0, 3);
      const remainingCount = Math.max(emailSyncResults.bookings.length - previewBookings.length, 0);

      blocks.push(
        <Card key="email-sync-results" variant="outlined">
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: theme.spacing(1),
            }}
          >
            <View>
              <SubHeading>Imported from {providerLabel}</SubHeading>
              <Caption color={theme.colors.textSecondary}>
                {formatDistanceToNow(new Date(emailSyncResults.timestamp), { addSuffix: true })}
              </Caption>
            </View>
            <Pressable onPress={handleDismissEmailSyncResults}>
              <Ionicons name="close" size={18} color={theme.colors.textSecondary} />
            </Pressable>
          </View>
          <Caption style={{ marginBottom: theme.spacing(1.5) }}>
            Added to {tripName}. Review highlights below.
          </Caption>
          <View style={{ gap: theme.spacing(1) }}>
            {previewBookings.map((booking, index) => {
              const iconName = BOOKING_TYPE_ICONS[booking.type || ''] || 'calendar';
              const bookingDate = formatBookingDate(booking);
              return (
                <View
                  key={`${booking.confirmationNumber || booking.provider || index}-${index}`}
                  style={{ flexDirection: 'row', gap: theme.spacing(1), alignItems: 'flex-start' }}
                >
                  <Ionicons name={iconName} size={20} color={theme.colors.primary} />
                  <View style={{ flex: 1 }}>
                    <Body style={{ fontWeight: '600' }}>
                      {booking.provider || booking.type || 'Booking'}
                    </Body>
                    {bookingDate && (
                      <Caption color={theme.colors.textSecondary}>{bookingDate}</Caption>
                    )}
                    {booking.location && (
                      <Caption color={theme.colors.textSecondary}>📍 {booking.location}</Caption>
                    )}
                  </View>
                </View>
              );
            })}
            {remainingCount > 0 && (
              <Caption color={theme.colors.textSecondary}>
                +{remainingCount} more booking{remainingCount === 1 ? '' : 's'} added
              </Caption>
            )}
          </View>
          <View
            style={{
              flexDirection: 'row',
              gap: theme.spacing(1),
              marginTop: theme.spacing(1.5),
            }}
          >
            <Button
              label="View trip"
              onPress={handleViewSyncedTrip}
              style={{ flex: 1 }}
            />
            <Button
              label="Dismiss"
              variant="secondary"
              onPress={handleDismissEmailSyncResults}
              style={{ flex: 1 }}
            />
          </View>
        </Card>
      );
    }

    if (!isActuallyNewUser && hiddenActiveTrip) {
      blocks.push(
        <ActiveTripPeek
          key="restore-active-trip"
          trip={hiddenActiveTrip}
          onExpand={handleExpandActiveTripCard}
          onDismiss={handleDismissActiveTripCard}
          briefingLine={hiddenActiveTrip.id === featuredTripHighlight?.trip.id ? featuredTripBriefing?.trayLine : undefined}
        />,
      );
    }

    if (!isActuallyNewUser && shouldShowHeaderCard && featuredTripHighlight) {
      blocks.push(
        <TripErrorBoundary key="active-trip-status-boundary" name="ActiveTripStatusCard">
        <ActiveTripStatusCard
          key="active-trip-status"
          trip={featuredTripHighlight.trip}
          nextItem={featuredTripHighlight.item ?? undefined}
          booking={matchedBooking ?? undefined}
          onOpenItinerary={(tripId) =>
            router.push({ pathname: '/(tabs)/plan', params: { tripId } })
          }
          onShareTrip={(trip) => setSelectedTripForCollaboration(trip)}
          alerts={featuredTripAlerts}
          briefing={featuredTripBriefing}
          showDismissControls
          onDismissCard={handleDismissActiveTripCard}
          onMinimizeCard={handleMinimizeActiveTripCard}
        />
        </TripErrorBoundary>,
      );
    }

    if (!isActuallyNewUser && shouldShowMinimizedHeaderChip && featuredTripHighlight) {
      blocks.push(
        <ActiveTripPeek
          key="active-trip-status-minimized"
          trip={featuredTripHighlight.trip}
          onExpand={handleExpandActiveTripCard}
          onDismiss={handleDismissActiveTripCard}
          briefingLine={featuredTripBriefing?.trayLine}
        />,
      );
    }

    if (tripsLoading && !isActuallyNewUser) {
      blocks.push(
        <Card key="loading-skeletons">
          <View style={{ gap: theme.spacing(1.5) }}>
            {Platform.OS === 'web' ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.spacing(1),
                  paddingHorizontal: theme.spacing(2),
                  paddingTop: theme.spacing(1),
                }}
              >
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Caption color={theme.colors.textSecondary}>Loading your trips...</Caption>
              </View>
            ) : null}
            <TripCardSkeleton />
            <TripCardSkeleton />
            <TripCardSkeleton />
          </View>
        </Card>,
      );
    }

    if (blocks.length === 0) {
      return null;
    }

    return (
      <View style={{ gap: theme.spacing(1.5) }}>
        {blocks}
      </View>
    );
  }, [
    greetingTitle,
    greetingSubtitle,
    emailSyncResults,
    tripById,
    featuredTripHighlight,
    isActuallyNewUser,
    tripsLoading,
    router,
    theme,
    shouldShowHeaderCard,
    shouldShowMinimizedHeaderChip,
    matchedBooking,
    handleDismissActiveTripCard,
    handleMinimizeActiveTripCard,
    handleExpandActiveTripCard,
    hiddenActiveTrip,
    handleDismissEmailSyncResults,
    handleViewSyncedTrip,
    formatBookingDate,
    featuredTripAlerts,
    featuredTripBriefing,
  ]);

  // Important: Do not reuse `renderItem` here. Its identity changes whenever trip rows/highlights refresh;
  // that would remap the footer props and repeatedly unmount/remount footer widgets (e.g. SavedRecommendationsList).
  const renderListFooter = useCallback(() => {
    if (tripsLoading) return null;

    const uid = firebaseUser?.uid;

    const quickActionsMargin = { marginTop: 0 as const };

    const quickActionsSection = (
      <View style={quickActionsMargin}>
        <QuickActionsWidget
          actions={[
            {
              id: 'email-import',
              label: 'Import bookings from email',
              icon: 'mail-outline',
              onPress: handleConnectEmail,
              status: inboxStatusLoading ? 'syncing' : connectedInboxProvider ? 'connected' : 'disconnected',
              actionLabel: inboxStatusLoading
                ? 'Checking...'
                : connectedInboxProvider
                  ? `Scan (${PROVIDER_LABELS[connectedInboxProvider]})`
                  : 'Connect',
            },
            {
              id: 'smart-upload',
              label: 'Smart upload travel docs',
              icon: 'cloud-upload-outline',
              onPress: () => setSmartUploadVisible(true),
            },
            {
              id: 'docs',
              label: 'Access your travel docs',
              icon: 'document-text-outline',
              onPress: () => router.push(getDocumentsTabTarget('merged')),
            },
            {
              id: 'maps',
              label: 'Download offline maps',
              icon: 'map-outline',
              onPress: handleOfflineMaps,
            },
          ]}
          onCreateTrip={
            !isActuallyNewUser
              ? () => {
                  if (!canStartNewTrip) {
                    openPremiumGateWithEmailSyncClosed({
                      feature: 'trip_limit',
                      title: '1 active trip on Free',
                      message:
                        'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
                      source: 'feature_gate',
                    });
                    return;
                  }
                  setShowManualForm(true);
                  setManualFormPrefill(null);
                }
              : undefined
          }
          onAskAI={!isActuallyNewUser ? handleAskTripFlowAi : undefined}
          onBrowseTemplates={!isActuallyNewUser ? handleBrowseTemplates : undefined}
          hasActiveTrips={hasActiveTrips}
        />
      </View>
    );

    const documentSection = uid ? (
      <View style={{ marginTop: theme.spacing(2) }}>
        {footerHeavyWidgetsEnabled ? (
          <DocumentExpirationWidget />
        ) : (
          <DeferredWidgetPlaceholder
            icon="document-text-outline"
            title="Document Expiration"
            description="Passport and visa reminders stay deferred until you scroll into this section."
          />
        )}
      </View>
    ) : null;

    const recommendationsSection = uid ? (
      <View style={{ marginTop: theme.spacing(2) }}>
        <Card
          variant="outlined"
          style={{
            backgroundColor: theme.colors.backgroundSecondary,
            borderColor: theme.colors.borderMuted,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1) }}>
            <View
              style={{
                width: 32,
                height: 32,
                borderRadius: theme.radii.pill,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.primary + '12',
              }}
            >
              <Ionicons name="list-outline" size={18} color={theme.colors.primary} />
            </View>
            <SubHeading>{BUCKET_LIST_TITLE}</SubHeading>
          </View>
          <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
            Quick access to destinations and ideas you’ve saved for later.
          </Body>
          {footerHeavyWidgetsEnabled ? (
            <SavedRecommendationsList
              userId={uid}
              maxItems={3}
              canCreateNewActiveTrip={canStartNewTrip}
              showCreateTripButton={true}
              showShareButton={true}
              showRemoveButton={true}
              onSelectRecommendation={handleBucketListItemPress}
              onCreateTripRecommendation={createTripFromRecommendation}
              onEmptyAskAi={handleAskTripFlowAi}
              onEmptyBrowseTemplates={handleBrowseTemplates}
              layout="horizontal"
              copyVariant="places"
            />
          ) : (
            <DeferredInlineWidgetPlaceholder message="Saved destinations stay deferred until you reach this part of the Trips screen." />
          )}
        </Card>
      </View>
    ) : null;

    const devToolsSection =
      __DEV__ && uid ? (
        <View style={{ marginTop: theme.spacing(2) }}>
          <Card variant="outlined" style={{ backgroundColor: theme.colors.warningContainer + '20', borderColor: theme.colors.warning + '40' }}>
            <View style={{ gap: theme.spacing(1.5) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="construct-outline" size={16} color={theme.colors.warning} />
                <Caption color={theme.colors.warning} style={{ fontWeight: '600' }}>
                  Development Tools
                </Caption>
              </View>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                <Button
                  label="Cleanup"
                  onPress={handleCleanupDuplicates}
                  disabled={cleanupInProgress}
                  variant="ghost"
                  style={{ flex: 1 }}
                  icon={<Ionicons name="trash-outline" size={18} color={theme.colors.error} />}
                />
              </View>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                ⚠️ Remove this section before production deployment
              </Caption>
            </View>
          </Card>
        </View>
      ) : null;

    return (
      <>
        {quickActionsSection}
        {documentSection}
        {recommendationsSection}
        {devToolsSection}
        {filteredTrips.length > 0 && (
          <View style={{ paddingTop: theme.spacing(2), paddingBottom: theme.spacing(2) }}>
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              Tripvio syncs your reservations, weather, and budget tools here once you start planning.
            </Caption>
          </View>
        )}
        <View style={{ marginTop: theme.spacing(2), paddingBottom: theme.spacing(2) }}>
          <HelpAndFeedbackCard />
        </View>
      </>
    );
  }, [
    tripsLoading,
    firebaseUser?.uid,
    filteredTrips.length,
    theme,
    handleConnectEmail,
    inboxStatusLoading,
    connectedInboxProvider,
    router,
    handleOfflineMaps,
    isActuallyNewUser,
    canStartNewTrip,
    openPremiumGateWithEmailSyncClosed,
    handleAskTripFlowAi,
    handleBrowseTemplates,
    hasActiveTrips,
    footerHeavyWidgetsEnabled,
    handleBucketListItemPress,
    createTripFromRecommendation,
    cleanupInProgress,
    handleCleanupDuplicates,
    setShowManualForm,
    setManualFormPrefill,
  ]);

  const tripsOfflineStaleDescriptor = (() => {
    if (isOnlineNow || tripsLoading || trips.length === 0) return null;
    const newestUpdatedAt = trips.reduce<number>((max, trip) => {
      const raw = (trip as Trip & { updatedAt?: unknown }).updatedAt;
      let ms = 0;
      if (raw instanceof Date) ms = raw.getTime();
      else if (typeof raw === 'string' || typeof raw === 'number') {
        const d = new Date(raw);
        ms = Number.isNaN(d.getTime()) ? 0 : d.getTime();
      }
      return Math.max(max, ms);
    }, 0);
    if (!newestUpdatedAt) return 'Showing cached trips while you are offline.';
    return `Showing cached trips updated ${new Date(newestUpdatedAt).toLocaleString()}.`;
  })();

  return (
    <Screen 
      title={title} 
      headerAction={
        firebaseUser?.uid ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            <View ref={newTripButtonRef} collapsable={false}>
              <Button
                label="New trip"
                size="small"
                variant="primary"
                icon={<Ionicons name="add" size={16} color={theme.colors.surface} />}
                onPress={handleNewTripAction}
              />
            </View>
            <NotificationBell
              count={alertCount}
              onPress={() => setShowNotifications(true)}
            />
          </View>
        ) : undefined
      }
      scrollable={false}
    >
      {tripsOfflineStaleDescriptor ? (
        <Card variant="outlined" style={{ marginBottom: theme.spacing(1) }}>
          <Caption color={theme.colors.textSecondary}>{tripsOfflineStaleDescriptor}</Caption>
        </Card>
      ) : null}
      <Profiler id="TripsScreen.List" onRender={onTripsProfilerRender}>
        {FlashList ? (
          <FlashList
            style={{ flex: 1, backgroundColor: theme.colors.background }}
            data={listData}
            renderItem={renderItem}
            keyExtractor={(item: any) => item.id}
            onViewableItemsChanged={onViewableItemsChanged.current}
            viewabilityConfig={viewabilityConfigRef.current}
            ListHeaderComponent={renderListHeader}
            ListHeaderComponentStyle={{ paddingBottom: theme.spacing(1.25) }}
            ListFooterComponent={renderListFooter}
            ListFooterComponentStyle={{ paddingTop: theme.spacing(1.25) }}
            onEndReached={handleFooterWidgetsNearViewport}
            onEndReachedThreshold={FOOTER_WIDGET_END_REACHED_THRESHOLD}
            ListEmptyComponent={
              tripsLoading ? null : (
                <Card>
                  <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingVertical: theme.spacing(3) }}>
                    {searchQuery
                      ? 'No trips match your search.'
                      : activeFilter === 'completed'
                        ? 'Complete your first trip'
                        : activeFilter === 'archived'
                          ? 'Your archived trips live here'
                      : hasArchivedTrips
                        ? 'All of your trips are archived. Unarchive a trip or start a new plan.'
                        : 'No trips yet. Create your first trip to get started!'}
                  </Body>
                </Card>
              )
            }
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
                tintColor={theme.colors.primary}
                colors={[theme.colors.primary]}
              />
            }
            contentContainerStyle={{
              backgroundColor: theme.colors.background,
              paddingTop: theme.spacing(0.5),
              paddingBottom: baseListPadding + sheetPadding,
            }}
            showsVerticalScrollIndicator={false}
            // FlashList-specific: required for good perf with variable-height rows.
            // Tuned to be close to TripCardEnhanced + headers/widgets average height.
            estimatedItemSize={240}
          />
        ) : (
          <FlatList
            style={{ flex: 1, backgroundColor: theme.colors.background }}
            // Prevent iOS from auto-adding safe-area/tab-bar insets which can appear
            // as a persistent blank strip above the tab bar during normal scrolling.
            contentInsetAdjustmentBehavior="never"
            ItemSeparatorComponent={() => <View style={{ height: theme.spacing(1.25) }} />}
            data={listData}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            onViewableItemsChanged={onViewableItemsChanged.current}
            viewabilityConfig={viewabilityConfigRef.current}
            ListHeaderComponent={renderListHeader}
            ListHeaderComponentStyle={{ paddingBottom: theme.spacing(1.25) }}
            ListFooterComponent={renderListFooter}
            ListFooterComponentStyle={{ paddingTop: theme.spacing(1.25) }}
            onEndReached={handleFooterWidgetsNearViewport}
            onEndReachedThreshold={FOOTER_WIDGET_END_REACHED_THRESHOLD}
            ListEmptyComponent={
              tripsLoading ? null : (
                <Card>
                  <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingVertical: theme.spacing(3) }}>
                    {searchQuery
                      ? 'No trips match your search.'
                      : activeFilter === 'completed'
                        ? 'Complete your first trip'
                        : activeFilter === 'archived'
                          ? 'Your archived trips live here'
                      : hasArchivedTrips
                        ? 'All of your trips are archived. Unarchive a trip or start a new plan.'
                        : 'No trips yet. Create your first trip to get started!'}
                  </Body>
                </Card>
              )
            }
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
                tintColor={theme.colors.primary}
                colors={[theme.colors.primary]}
              />
            }
            contentContainerStyle={{
              backgroundColor: theme.colors.background,
              paddingTop: theme.spacing(0.5),
              paddingBottom: baseListPadding + sheetPadding,
            }}
            showsVerticalScrollIndicator={false}
            // Performance optimizations
            removeClippedSubviews={true}
            maxToRenderPerBatch={10}
            windowSize={10}
            initialNumToRender={10}
            updateCellsBatchingPeriod={50} // Batch updates every 50ms for smoother scrolling
            // Note: getItemLayout removed due to variable item heights
            // FlatList will calculate layouts automatically for better flexibility
          />
        )}
      </Profiler>

      {shouldShowBottomSheet && featuredTripHighlight && (
        <ActiveTripBottomSheet
          trip={featuredTripHighlight.trip}
          nextItem={featuredTripHighlight.item ?? undefined}
          booking={matchedBooking ?? undefined}
          briefing={featuredTripBriefing}
          onShareTrip={(trip) => setSelectedTripForCollaboration(trip)}
          onOpenItinerary={(tripId) =>
            router.push({
              pathname: '/(tabs)/plan',
              params: { tripId },
            })
          }
          onDismiss={handleDismissActiveTripCard}
          onMinimize={handleMinimizeActiveTripCard}
        />
      )}

      {supportsBottomSheet && featuredTripHighlight && featuredTripCardVisibility === 'minimized' && (
        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            bottom: theme.spacing(3),
            right: theme.spacing(2),
            left: theme.spacing(2),
            alignItems: 'flex-end',
          }}
        >
          <ActiveTripPeek
            trip={featuredTripHighlight.trip}
            onExpand={handleExpandActiveTripCard}
            onDismiss={handleDismissActiveTripCard}
            briefingLine={featuredTripBriefing?.trayLine}
            variant="floating"
            style={{
              ...theme.shadows.level1,
            }}
          />
        </View>
      )}

      {tripDetectionSession &&
        inboxConnections[tripDetectionSession.provider] === true &&
        Boolean(tripDetectionSession.taskId) && (
        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            left: theme.spacing(2),
            right: theme.spacing(2),
            bottom: theme.spacing(3),
            zIndex: 1200,
          }}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Trip detection in progress"
            accessibilityHint="Tap to reopen detected trips"
            onPress={() => {
              if (inboxConnections[tripDetectionSession.provider] !== true) {
                clearTripDetectionSession();
                return;
              }
              router.push({
                pathname: '/trip-detection',
                params: {
                  provider: tripDetectionSession.provider,
                  taskId: tripDetectionSession.taskId,
                },
              });
            }}
            style={({ pressed }) => ({
              backgroundColor: theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              borderRadius: theme.radii.lg,
              padding: theme.spacing(1.5),
              opacity: pressed ? 0.92 : 1,
              ...theme.shadows.level2,
            })}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1, minWidth: 0 }}>
                <Ionicons name="sparkles-outline" size={20} color={theme.colors.primary} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <ThemedText variant="bodyStrong" numberOfLines={1}>
                    {tripDetectionSession.label || 'Trip detection in progress'}
                  </ThemedText>
                  <Caption color={theme.colors.textSecondary}>
                    Tap to reopen details
                  </Caption>
                </View>
              </View>
              <Pressable
                hitSlop={8}
                onPress={(event) => {
                  event.stopPropagation();
                  clearTripDetectionSession();
                }}
                accessibilityRole="button"
                accessibilityLabel="Dismiss trip detection banner"
                style={({ pressed }) => ({
                  padding: theme.spacing(0.5),
                  borderRadius: 999,
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <Ionicons name="close" size={18} color={theme.colors.textSecondary} />
              </Pressable>
            </View>
          </Pressable>
        </View>
      )}


      {firebaseUser?.uid && (
        <>
          <NewTripOptionsModal
            visible={showNewTripOptions}
            onClose={() => setShowNewTripOptions(false)}
            onPlanManually={() => {
              setShowNewTripOptions(false);
              if (!canStartNewTrip) {
                openPremiumGateWithEmailSyncClosed({
                  feature: 'trip_limit',
                  title: '1 active trip on Free',
                  message:
                    'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
                  source: 'feature_gate',
                });
                return;
              }
              setShowManualForm(true);
              setManualFormPrefill(null);
            }}
            onPlanWithAI={() => {
              setShowNewTripOptions(false);
              handleAskTripFlowAi();
            }}
            onConnectEmail={() => {
              setShowNewTripOptions(false);
              if (!canStartNewTrip) {
                openPremiumGateWithEmailSyncClosed({
                  feature: 'trip_limit',
                  title: '1 active trip on Free',
                  message:
                    'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
                  source: 'feature_gate',
                });
                return;
              }
              handleConnectEmail();
            }}
            onSmartUpload={() => {
              setShowNewTripOptions(false);
              setSmartUploadVisible(true);
            }}
          />

          <ManualTripForm
            visible={showManualForm}
            onClose={() => {
              setShowManualForm(false);
              setManualFormPrefill(null);
            }}
            onSuccess={() => {
              // Trips will automatically update via subscription
              showSuccess('Your trip has been created successfully!');
              ratingTriggerSourceRef.current = 'manual_trip_create';
              setTripCreatedAnimationMessage('Trip created\nYour trip is coming together.');
              setTripCreatedAnimationVisible(true);
            }}
            userId={firebaseUser.uid}
            initialValues={manualFormPrefill}
          />
          {firebaseUser?.uid && (
            <TripErrorBoundary name="AITravelAssistant">
              <Suspense fallback={null}>
                <AITravelAssistantModal
                  visible={showAIAssistant}
                  onClose={() => setShowAIAssistant(false)}
                  onSelectRecommendation={handleSelectRecommendation}
                  userId={firebaseUser.uid}
                />
              </Suspense>
            </TripErrorBoundary>
          )}
          {selectedTripForCollaboration && (
            <CollaborationModal
              visible={!!selectedTripForCollaboration}
              onClose={() => setSelectedTripForCollaboration(null)}
              trip={selectedTripForCollaboration}
              currentUserId={firebaseUser.uid}
              onCollaboratorsUpdated={() => {
                // Trips will automatically update via subscription
              }}
              onLeftTrip={() => setSelectedTripForCollaboration(null)}
            />
          )}
          {selectedTripForActions && firebaseUser?.uid && (
            <Suspense fallback={null}>
              <TripActionsModal
                visible={!!selectedTripForActions}
                onClose={() => setSelectedTripForActions(null)}
                trip={selectedTripForActions}
                currentUserId={firebaseUser.uid}
                onActionComplete={(completion: TripActionsModalCompletion) => {
                  const uid = firebaseUser.uid;
                  const sel = selectedTripForActions;
                  if (completion === 'delete' && sel) {
                    setTrips((prev) => prev.filter((t) => t.id !== sel.id));
                    return;
                  }
                  void refreshTripListFromServer(uid).then((nextTrips) => setTrips(nextTrips));
                }}
                onEditPress={() => {
                  setSelectedTripForEdit(selectedTripForActions);
                }}
                onOpenTemplateBuilder={(tripId) => {
                  setSelectedTripForActions(null);
                  setActiveTemplateModal('builder');
                  setTemplateBuilderSourceTripId(tripId);
                }}
              />
            </Suspense>
          )}
          {selectedTripForEdit && firebaseUser?.uid && (
            <EditTripForm
              visible={!!selectedTripForEdit}
              onClose={() => setSelectedTripForEdit(null)}
              trip={selectedTripForEdit}
              userId={firebaseUser.uid}
              onSuccess={() => {
                // Toasts and trip list come from EditTripForm + subscribeToUserTrips
              }}
            />
          )}

        {firebaseUser?.uid && (
          <TripErrorBoundary name="SmartUploadModal">
            <Suspense fallback={null}>
              <SmartUploadModalLazy
                visible={smartUploadVisible}
                onClose={() => setSmartUploadVisible(false)}
                initialTripId={undefined}
                trips={trips}
                userId={firebaseUser.uid}
                onTripCreated={(tripId) => {
                  showSuccess('Trip created from smart upload.');
                  ratingTriggerSourceRef.current = 'smart_upload_trip_create';
                  setTripCreatedAnimationMessage('Trip created\nYour trip is coming together.');
                  setTripCreatedAnimationVisible(true);
                  router.push({ pathname: '/(tabs)/plan', params: { tripId } });
                }}
                onTripSelected={(tripId) => {
                  if (tripId) {
                    router.push({ pathname: '/(tabs)/plan', params: { tripId } });
                  }
                }}
                onItemsCreated={({ tripId, createdIds, failures }) => {
                  if (createdIds.length) {
                    showToastWithOptions({
                      message: `Added ${createdIds.length} item${createdIds.length === 1 ? '' : 's'} to itinerary.`,
                      type: 'success',
                      details: 'You can edit or undo anything at any time.',
                      actions: [
                        {
                          label: 'View',
                          onPress: () => {
                            setSmartUploadVisible(false);
                            router.push({ pathname: '/(tabs)/plan', params: { tripId, itemId: createdIds[0] } });
                          },
                          accessibilityLabel: 'View created itinerary item',
                        },
                        {
                          label: 'Edit',
                          onPress: () => {
                            setSmartUploadVisible(false);
                            router.push({ pathname: '/(tabs)/plan', params: { tripId, itemId: createdIds[0] } });
                          },
                          accessibilityLabel: 'Edit created itinerary item',
                        },
                      ],
                      onUndo: () => {
                        void (async () => {
                          try {
                            const { deleteItineraryItem } = await import('@/services/itinerary');
                            const results = await Promise.allSettled(
                              createdIds.map((id) => deleteItineraryItem(tripId, id)),
                            );
                            const failed = results.filter((r) => r.status === 'rejected').length;
                            if (failed) {
                              showError(`Undo completed with ${failed} failure${failed === 1 ? '' : 's'}.`);
                            } else {
                              showSuccess('Undone.');
                            }
                          } catch {
                            showError('Unable to undo. Please try again.');
                          }
                        })();
                      },
                    });
                  }
                  if (failures.length) {
                    showError(`Unable to create ${failures.length} item${failures.length === 1 ? '' : 's'}.`);
                  }
                }}
              />
            </Suspense>
          </TripErrorBoundary>
          )}

          <EmailProviderSelectionModal
            visible={showEmailProviderSelection}
            onClose={() => setShowEmailProviderSelection(false)}
            onNativeDismiss={onEmailProviderModalNativeDismiss}
            connectingProvider={connectingProvider}
            disconnectingProvider={disconnectingProvider}
            providerConnections={inboxConnections}
            onConnectProvider={handleConnectProvider}
            onDisconnectProvider={handleDisconnectProvider}
          />

          <Modal
            visible={!!connectionProgress}
            transparent
            animationType="fade"
            onRequestClose={() => setConnectionProgress(null)}
          >
            <View
              style={{
                flex: 1,
                backgroundColor: 'rgba(0,0,0,0.55)',
                justifyContent: 'center',
                alignItems: 'center',
                padding: theme.spacing(3),
              }}
            >
              <Card style={{ width: '100%', maxWidth: 380 }}>
                <View style={{ alignItems: 'center', gap: theme.spacing(1.5), paddingVertical: theme.spacing(1) }}>
                  <ActivityIndicator size="large" color={theme.colors.primary} />
                  <SubHeading style={{ textAlign: 'center' }}>
                    {connectionProgress?.phase === 'verifying'
                      ? `Finishing ${connectionProgress.provider === 'icloud' ? 'mailbox' : connectionProgress.provider} connection`
                      : 'Starting trip detection'}
                  </SubHeading>
                  <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                    {connectionProgress?.phase === 'verifying'
                      ? 'We are verifying your email connection and getting your inbox ready.'
                      : 'Scanning your inbox for bookings now. Results will appear automatically.'}
                  </Body>
                  <Pressable
                    onPress={() => setConnectionProgress(null)}
                    accessibilityRole="button"
                    accessibilityLabel="Close"
                    style={{ marginTop: theme.spacing(1), paddingVertical: theme.spacing(0.5) }}
                  >
                    <Caption style={{ textAlign: 'center', color: theme.colors.primary }}>Close</Caption>
                  </Pressable>
                </View>
              </Card>
            </View>
          </Modal>

          <SuccessAnimation
            visible={tripCreatedAnimationVisible}
            message={tripCreatedAnimationMessage}
            onComplete={() => {
              setTripCreatedAnimationVisible(false);
              if (!ratingTriggerSourceRef.current) {
                return;
              }
              const source = ratingTriggerSourceRef.current;
              ratingTriggerSourceRef.current = null;
              void maybePromptForRating({
                trigger: 'trip_creation_success',
                source,
                deferMs: 300,
              });
            }}
          />
        </>
      )}

      {emailSyncTripId && (
        <TripErrorBoundary name="EmailSyncStatus">
          <Suspense fallback={null}>
            <EmailSyncStatusModal
              visible={showEmailSyncStatus}
              onClose={() => {
                setShowEmailSyncStatus(false);
                setEmailSyncTripId(null);
              }}
              tripId={emailSyncTripId}
              provider={emailSyncProvider}
              syncSource="trips"
              onSyncComplete={(bookings) => {
                if (emailSyncTripId) {
                  setEmailSyncResults({
                    tripId: emailSyncTripId,
                    provider: emailSyncProvider,
                    bookings,
                    timestamp: Date.now(),
                  });
                }
                setShowEmailSyncStatus(false);
                setEmailSyncTripId(null);
                if (bookings.length > 0) {
                  showSuccess(`Imported ${bookings.length} booking${bookings.length === 1 ? '' : 's'} from Gmail.`);
                } else {
                  showInfo('No new bookings found in Gmail.');
                }
              }}
            />
          </Suspense>
        </TripErrorBoundary>
      )}

      {/* Email Sync Results Modal - for quick actions sync */}
      {emailSyncResultsData && (
        <Suspense fallback={null}>
          <EmailSyncResultsModalLazy
            visible={showEmailSyncResultsModal}
            onClose={() => {
              setShowEmailSyncResultsModal(false);
              setEmailSyncResultsData(null);
            }}
            bookings={emailSyncResultsData.bookings}
            bookingsForOtherTrips={emailSyncResultsData.bookingsForOtherTrips}
            provider={emailSyncResultsData.provider}
            existingTrips={activeTrips}
          />
        </Suspense>
      )}

      {/* Notifications Modal */}
      {firebaseUser?.uid && (
        <NotificationModal
          visible={showNotifications}
          onClose={() => setShowNotifications(false)}
          notifications={tripNotifications}
          onAction={handleNotificationAction}
          onDismiss={(notification) => handleClearAlert(notification.id)}
          onDismissAll={handleClearAll}
          title="Trip Alerts"
        />
      )}

      {/* Template Browser */}
      <TemplateBrowser
        visible={showTemplateBrowser}
        initialTopicTag={templateBrowserInitialTopic}
        deeplinkRef={templateDeeplinkRef}
        deeplinkUtmSource={templateDeeplinkUtmSource}
        onInitialTopicConsumed={handleTemplateBrowserInitialTopicConsumed}
        onDeeplinkAttributionConsumed={handleTemplateDeeplinkAttributionConsumed}
        onClose={() => {
          setTemplateBrowserInitialTopic(undefined);
          setTemplateDeeplinkRef(undefined);
          setTemplateDeeplinkUtmSource(undefined);
          setActiveTemplateModal(null);
        }}
        onDismiss={() => {
          if (pendingTemplateTransitionRef.current === 'openDetail') {
            pendingTemplateTransitionRef.current = null;
            setActiveTemplateModal('detail');
          }
        }}
        onSelectTemplate={handleSelectTemplate}
        onCreateTemplate={() => {
          pendingTemplateTransitionRef.current = null;
          setTemplateBrowserInitialTopic(undefined);
          setActiveTemplateModal(null);
          setActiveTemplateModal('builder');
          setTemplateBuilderSourceTripId(undefined);
        }}
      />

      {/* Template Builder */}
      {showTemplateBuilder && (
        <Suspense fallback={null}>
          <TemplateBuilderLazy
            visible={showTemplateBuilder}
            onClose={() => {
              setActiveTemplateModal(null);
              setTemplateBuilderSourceTripId(undefined);
            }}
            onSuccess={(templateId) => {
              console.log('[TripsScreen] Template created:', templateId);
              // Optionally refresh templates or show success message
              setActiveTemplateModal(null);
              setTemplateBuilderSourceTripId(undefined);
            }}
            sourceTripId={templateBuilderSourceTripId}
          />
        </Suspense>
      )}

      {/* Template Detail Modal */}
      <TemplateDetailModal
        visible={showTemplateDetail || showTemplateCustomization}
        template={selectedTemplate}
        onClose={() => {
          if (showTemplateCustomization) {
            handleCloseTemplateCustomization('user');
            return;
          }
          // This is called when user manually dismisses the modal (swipe down / back button)
          // NOT when we programmatically set visible={false}
          const shouldOpenBrowser = templateEntryPoint === 'browser';
          
          setActiveTemplateModal(null);
          
          setSelectedTemplate(null);
          setSelectedTemplateTopicTag(undefined);
          setTemplateEntryPoint(null);
          
          if (shouldOpenBrowser) {
            pendingTemplateTransitionRef.current = 'reopenBrowser';
          }
        }}
        onDismiss={() => {
          if (pendingTemplateTransitionRef.current === 'navigateToPlan') {
            const tripId = pendingTemplateRouteTripIdRef.current;
            pendingTemplateTransitionRef.current = null;
            pendingTemplateRouteTripIdRef.current = null;
            templateCustomizationShouldClearOnDismissRef.current = false;
            setSelectedTemplate(null);
            setSelectedTemplateTopicTag(undefined);
            setTemplateEntryPoint(null);
            if (tripId) {
              requestAnimationFrame(() => {
                router.push({
                  pathname: '/(tabs)/plan',
                  params: { tripId },
                });
              });
            }
            return;
          }
          if (pendingTemplateTransitionRef.current === 'reopenBrowser') {
            pendingTemplateTransitionRef.current = null;
            templateCustomizationShouldClearOnDismissRef.current = false;
            setActiveTemplateModal('browser');
            setSelectedTemplate(null);
            setSelectedTemplateTopicTag(undefined);
            setTemplateEntryPoint(null);
            return;
          }
          if (pendingTemplateTransitionRef.current === 'showTripLimitGate') {
            pendingTemplateTransitionRef.current = null;
            templateCustomizationShouldClearOnDismissRef.current = false;
            setSelectedTemplate(null);
            setSelectedTemplateTopicTag(undefined);
            setTemplateEntryPoint(null);
            presentTripLimitGate();
            return;
          }
          if (templateCustomizationShouldClearOnDismissRef.current) {
            templateCustomizationShouldClearOnDismissRef.current = false;
            setSelectedTemplate(null);
            setSelectedTemplateTopicTag(undefined);
            setTemplateEntryPoint(null);
          }
        }}
        onUseTemplate={handleUseTemplate}
        mode={showTemplateCustomization ? 'customization' : 'detail'}
        customizationContent={
          showTemplateCustomization ? (
            <TemplateCustomizationModal
              visible
              embedded
              template={selectedTemplate}
              onClose={handleCloseTemplateCustomization}
              onConfirm={handleConfirmTemplateCustomization}
            />
          ) : undefined
        }
      />

      {/* Saved Recommendation Detail Modal (AI Bucket List items) */}
      <SavedRecommendationDetailModal
        visible={showSavedRecommendationDetail}
        recommendation={selectedSavedRecommendation}
        onClose={() => {
          setShowSavedRecommendationDetail(false);
          setSelectedSavedRecommendation(null);
        }}
      />

    </Screen>
  );
}

function ArchivedTripCard({ trip, isLast, onActionsPress }: { trip: TripType; isLast: boolean; onActionsPress?: (trip: TripType) => void }) {
  const theme = useTheme();
  const router = useRouter();
  const formattedDates = formatTripDates(trip.startDate, trip.endDate);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const isOwner = trip.owner === firebaseUser?.uid;
  
  // Get image URL - use saved landmarkImage or fallback to getting one from destination
  // For multi-destination trips, this will pick the first city
  const imageUrl = trip.landmarkImage || (trip.destination ? getLandmarkImage(trip.destination) : null);

  const handlePress = () => {
    // Navigate to plan tab with tripId parameter
    router.push({
      pathname: '/(tabs)/plan',
      params: { tripId: trip.id },
    });
  };

  const handleActionsPress = (e: any) => {
    e.stopPropagation();
    onActionsPress?.(trip);
  };

  return (
    <Pressable
      onPress={handlePress}
      style={({ pressed }) => ({
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <View
        style={{
          flexDirection: 'row',
          gap: theme.spacing(2),
          paddingBottom: isLast ? 0 : theme.spacing(1.5),
          borderBottomWidth: isLast ? 0 : 1,
          borderBottomColor: theme.colors.borderMuted,
        }}
      >
        {/* Landmark Image */}
        <View
          style={{
            width: 80,
            height: 80,
            borderRadius: theme.radii.md,
            overflow: 'hidden',
            backgroundColor: theme.colors.borderMuted,
            opacity: 0.6,
          }}
        >
          {imageUrl ? (
            <Image
              source={{ uri: imageUrl }}
              style={{ width: '100%', height: '100%' }}
              resizeMode="cover"
            />
          ) : (
            <View
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: theme.colors.borderMuted,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Ionicons name="image-outline" size={32} color={theme.colors.textSecondary} />
            </View>
          )}
        </View>

        {/* Trip Details */}
        <View style={{ flex: 1, gap: theme.spacing(0.5), justifyContent: 'center' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 16, fontWeight: '600', flex: 1 }}>
              {trip.name} [Archived]
            </ThemedText>
            {isOwner && (
              <Pressable
                onPress={handleActionsPress}
                style={{
                  padding: theme.spacing(0.75),
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.borderMuted,
                }}
              >
                <Ionicons name="ellipsis-horizontal" size={18} color={theme.colors.textSecondary} />
              </Pressable>
            )}
          </View>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
            {formattedDates}
          </Caption>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, fontStyle: 'italic' }}>
            Read-only access
          </Caption>
        </View>
      </View>
    </Pressable>
  );
}

function QuickLink({ label, onPress, actionLabel }: { label: string; onPress?: () => void; actionLabel?: string }) {
  const theme = useTheme();
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: theme.spacing(1),
        opacity: pressed ? 0.7 : (onPress ? 1 : 0.5),
      })}
      disabled={!onPress}
    >
      <Body>{label}</Body>
      <Caption color={onPress ? theme.colors.primary : theme.colors.textSecondary}>
        {actionLabel || (onPress ? 'Sync' : 'Configure')}
      </Caption>
    </Pressable>
  );
}

export default function TripsScreen() {
  return (
    <TripErrorBoundary name="HomeTrips">
      <TripsScreenInner />
    </TripErrorBoundary>
  );
}
